import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import io from 'socket.io-client';
import { Room, RoomEvent, Track, createLocalAudioTrack, createLocalVideoTrack } from 'livekit-client';
import { cie10Catalog, mockAppointments, mockNotes } from '../mockData';
import { HCU_002_MODULES, getFormCompletionSummary, getModuleCompletion } from '../clinical/mspFormRegistry';
import { auditFetch } from '../utils/auditFetch';
import ClinicalFilesPanel from '../components/ClinicalFilesPanel';
import DigitalPrescriptionPanel from '../components/DigitalPrescriptionPanel';
import MedicalOrdersPanel from '../components/MedicalOrdersPanel';
import MedicalCertificatesPanel from '../components/MedicalCertificatesPanel';
import InterconsultationReferralPanel from '../components/InterconsultationReferralPanel';
import PreTriagePanel from '../components/PreTriagePanel';
import { getTriagePriorityLabel } from '../clinical/preTriage';
import ClinicalAlertsPanel from '../components/ClinicalAlertsPanel';
import { generateClinicalAlerts } from '../clinical/clinicalAlerts';
import '../styles/ConsultationRoom.css';

const FORM_TYPE = 'HCU_002_CONSULTA_EXTERNA';
const DEFAULT_VIDEO_PROVIDER = String(process.env.REACT_APP_VIDEO_PROVIDER || 'p2p').trim().toLowerCase();

const CONNECTION_LABELS = {
  connecting: 'Conectando',
  ready: 'En espera',
  active: 'Conectado',
  reconnecting: 'Reconectando',
  ended: 'Finalizada',
  error: 'Sin conexion',
};

const CONNECTION_DETAILS = {
  connecting: 'Preparando camara, microfono y sala segura.',
  ready: 'Sala lista. Esperando al otro participante.',
  active: 'Audio y video activos para la consulta.',
  reconnecting: 'Intentando recuperar la conexion.',
  ended: 'La consulta fue finalizada.',
  error: 'Revisa permisos de camara, microfono o red.',
};

const historyOptions = [
  ['cardiopathy', 'Cardiopatia'],
  ['hypertension', 'Hipertension'],
  ['cerebrovascular', 'Enf. cerebro vascular'],
  ['endocrineMetabolic', 'Endocrino metabolico'],
  ['cancer', 'Cancer'],
  ['tuberculosis', 'Tuberculosis'],
  ['mentalIllness', 'Enf. mental'],
  ['infectiousDisease', 'Enf. infecciosa'],
  ['malformation', 'Malformacion'],
  ['other', 'Otro'],
];

const organSystemOptions = [
  ['skinAnnexes', 'Piel / anexos'],
  ['senseOrgans', 'Organos de los sentidos'],
  ['respiratory', 'Respiratorio'],
  ['cardiovascular', 'Cardio vascular'],
  ['digestive', 'Digestivo'],
  ['genitourinary', 'Genito urinario'],
  ['musculoskeletal', 'Musculo esqueletico'],
  ['endocrine', 'Endocrino'],
  ['hemolymphatic', 'Hemo linfatico'],
  ['nervous', 'Nervioso'],
];

const regionalExamOptions = [
  ['skinFanera', 'Piel / faneras'],
  ['head', 'Cabeza'],
  ['eyes', 'Ojos'],
  ['ears', 'Oidos'],
  ['nose', 'Nariz'],
  ['mouth', 'Boca'],
  ['oropharynx', 'Orofaringe'],
  ['neck', 'Cuello'],
  ['axillaBreasts', 'Axilas / mamas'],
  ['thorax', 'Torax'],
  ['abdomen', 'Abdomen'],
  ['spine', 'Columna vertebral'],
  ['groinPerineum', 'Ingle / perine'],
  ['upperLimbs', 'Miembros superiores'],
  ['lowerLimbs', 'Miembros inferiores'],
];

const systemicExamOptions = [
  ['senseOrgans', 'Organos de los sentidos'],
  ['respiratory', 'Respiratorio'],
  ['cardiovascular', 'Cardio vascular'],
  ['digestive', 'Digestivo'],
  ['genital', 'Genital'],
  ['urinary', 'Urinario'],
  ['musculoskeletal', 'Musculo esqueletico'],
  ['endocrine', 'Endocrino'],
  ['hemolymphatic', 'Hemo linfatico'],
  ['neurological', 'Neurologico'],
];

const createChecklist = (options) => options.reduce((acc, [key]) => ({
  ...acc,
  [key]: { checked: false, description: '' },
}), {});

const parseNumber = (value) => {
  const normalized = String(value || '').replace(',', '.').replace(/[^\d.]/g, '');
  const number = Number.parseFloat(normalized);
  return Number.isFinite(number) ? number : null;
};

const calculateAge = (birthDate) => {
  if (!birthDate) return '';
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 ? String(age) : '';
};

const toDateInputValue = (date = new Date()) => date.toISOString().slice(0, 10);
const toTimeInputValue = (date = new Date()) => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

const calculateBmi = (weightValue, heightValue) => {
  const weight = parseNumber(weightValue);
  const height = parseNumber(heightValue);
  const meters = height && height > 3 ? height / 100 : height;
  return weight && meters ? (weight / (meters * meters)).toFixed(1) : '';
};

const createEmptyDiagnosis = () => ({
  description: '',
  cie10Code: '',
  type: 'presuntivo',
  classification: 'secondary',
  observations: '',
  searchTerm: '',
});

const getDiagnosisCode = (diagnosis) => diagnosis.cie10Code || diagnosis.cie || '';
const findCie10ByCode = (code) => cie10Catalog.find((item) => item.code === String(code || '').trim().toUpperCase());
const diagnosisHasContent = (diagnosis) => Boolean(
  String(diagnosis.description || '').trim()
  || String(getDiagnosisCode(diagnosis) || '').trim()
  || String(diagnosis.observations || '').trim()
);

const isValidCie10Code = (code) => /^[A-Z][0-9]{2}(\.[0-9A-Z]{1,2})?$/.test(String(code || '').trim().toUpperCase());

const searchCie10Catalog = (term) => {
  const query = String(term || '').trim().toLowerCase();
  if (!query) return cie10Catalog.slice(0, 8);
  return cie10Catalog
    .filter((item) => `${item.code} ${item.description}`.toLowerCase().includes(query))
    .slice(0, 8);
};

const createInitialHcuForm = ({ appointment, user }) => {
  const patient = appointment.patient || {};
  const doctor = appointment.doctor || user || {};
  const now = new Date();
  const nameParts = (patient.name || '').trim().split(/\s+/);
  const weight = parseNumber(patient.weight)?.toString() || '';
  const height = parseNumber(patient.height)?.toString() || '';

  return {
    formType: FORM_TYPE,
    recordStatus: 'draft',
    facilityPatientData: {
      institution: 'Sistema Nacional de Salud',
      unicodigo: '',
      healthFacility: 'TeleMec',
      clinicalHistoryNumber: patient.clinicalHistoryNumber || patient.id || '',
      fileNumber: patient.fileNumber || '',
      sheetNumber: '1',
      documentId: patient.documentId || '',
      firstLastName: patient.firstLastName || nameParts[1] || '',
      secondLastName: patient.secondLastName || nameParts[2] || '',
      firstName: patient.firstName || nameParts[0] || patient.name || '',
      secondName: patient.secondName || nameParts.slice(3).join(' '),
      fullName: patient.name || '',
      sex: patient.sex || '',
      age: calculateAge(patient.birthDate),
    },
    consultationReason: {
      type: 'primera',
      description: appointment.reason || mockNotes[appointment.id]?.consultation || '',
    },
    personalHistory: {
      items: createChecklist(historyOptions),
      clinicalDetails: patient.medicalHistory || mockNotes[appointment.id]?.patientHistory || '',
    },
    familyHistory: {
      items: createChecklist(historyOptions),
      clinicalDetails: patient.familyHistory || '',
    },
    currentIllness: mockNotes[appointment.id]?.consultation || appointment.reason || '',
    vitalSigns: {
      date: toDateInputValue(now),
      time: toTimeInputValue(now),
      temperature: parseNumber(patient.temperature)?.toString() || '',
      bloodPressure: patient.bloodPressure || '',
      pulse: patient.pulse || '',
      respiratoryRate: patient.respiratoryRate || '',
      weight,
      height,
      bmi: calculateBmi(weight, height),
      abdominalPerimeter: patient.abdominalPerimeter || '',
      capillaryHemoglobin: patient.capillaryHemoglobin || '',
      capillaryGlucose: patient.capillaryGlucose || '',
      pulseOximetry: patient.pulseOximetry || '',
    },
    organSystemsReview: createChecklist(organSystemOptions),
    physicalExam: {
      regional: createChecklist(regionalExamOptions),
      systemic: createChecklist(systemicExamOptions),
    },
    diagnoses: [{ ...createEmptyDiagnosis(), classification: 'primary' }],
    treatmentPlan: {
      diagnostic: '',
      therapeutic: mockNotes[appointment.id]?.recommendations || '',
      educational: '',
      recommendations: mockNotes[appointment.id]?.recommendations || '',
    },
    clinicalFollowUp: {
      evolution: '',
      requiresNextAppointment: false,
      nextAppointmentDate: '',
      nextAppointmentTime: '',
      nextAppointmentReason: '',
      nextAppointmentMode: 'telemedicina',
      nextAppointmentNotes: '',
    },
    responsibleProfessional: {
      date: toDateInputValue(now),
      time: toTimeInputValue(now),
      name: doctor.name || user.name || '',
      documentId: doctor.documentId || user.documentId || '',
      signature: doctor.name || user.name || '',
      stamp: doctor.specialty || user.specialty || 'Medicina General',
    },
  };
};

const ConsultationRoom = ({ user }) => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const appointmentData = location.state?.appointment || mockAppointments.find((apt) => apt.id === appointmentId) || mockAppointments[0];
  const appointment = appointmentData;
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const displayMode = queryParams.get('display') || 'full';
  const isMonitorView = displayMode === 'monitor';
  const isClinicalOnlyView = displayMode === 'clinical';

  const [otherUser, setOtherUser] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [consultationStatus, setConsultationStatus] = useState('connecting');
  const [hcuForm, setHcuForm] = useState(() => createInitialHcuForm({ appointment, user }));
  const [openSections, setOpenSections] = useState({
    patient: true,
    reason: true,
    history: false,
    vitals: false,
    systems: false,
    exam: false,
    diagnosis: true,
    plan: false,
    followUp: false,
  });
  const [formErrors, setFormErrors] = useState([]);
  const [saveMessage, setSaveMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [callSeconds, setCallSeconds] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatText, setChatText] = useState('');
  const [chatSaveStatus, setChatSaveStatus] = useState('');
  const [isSendingChat, setIsSendingChat] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [latestClinicalFileAlert, setLatestClinicalFileAlert] = useState(null);
  const [clinicalFileEvents, setClinicalFileEvents] = useState([]);
  const [activeSidePanel, setActiveSidePanel] = useState(user.role === 'doctor' ? 'admission' : 'files');
  const [isVideoFloating, setIsVideoFloating] = useState(false);
  const [isDoctorMenuCollapsed, setIsDoctorMenuCollapsed] = useState(true);
  const [clinicalFilesRefresh, setClinicalFilesRefresh] = useState(0);
  const [patientProfile, setPatientProfile] = useState(null);
  const [previousRecords, setPreviousRecords] = useState([]);
  const [quickActionMessage, setQuickActionMessage] = useState('');
  const [consultationSummary, setConsultationSummary] = useState(null);
  const [admissionContext, setAdmissionContext] = useState(null);
  const [preTriageContext, setPreTriageContext] = useState(appointment.preTriage || null);
  const [videoProvider, setVideoProvider] = useState(DEFAULT_VIDEO_PROVIDER);
  const [videoProviderId, setVideoProviderId] = useState('');
  const [videoProviderReady, setVideoProviderReady] = useState(false);
  const [recordingSettings, setRecordingSettings] = useState({ enabled: false, autoStartWithConsent: false, storageMode: 'local' });
  const [recordingState, setRecordingState] = useState({ status: 'idle', message: 'Grabacion no iniciada', recordingId: '' });

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const chatPanelRef = useRef(null);
  const prescriptionPanelRef = useRef(null);
  const medicalOrdersPanelRef = useRef(null);
  const medicalCertificatesPanelRef = useRef(null);
  const referralPanelRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const liveKitRoomRef = useRef(null);
  const streamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const socketRef = useRef(null);
  const iceCandidatesQueueRef = useRef([]);
  const localMediaPromiseRef = useRef(null);
  const hasJoinedConsultationRef = useRef(false);
  const autoRecordingStartedRef = useRef(false);
  const localVideoTrackRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const remoteVideoTrackRef = useRef(null);
  const remoteAudioTrackRef = useRef(null);
  const liveKitConnectPromiseRef = useRef(null);

  const patient = useMemo(() => appointment.patient || {}, [appointment.patient]);
  const doctor = useMemo(() => appointment.doctor || {}, [appointment.doctor]);
  const isDoctor = user.role === 'doctor';
  const isLiveKitProvider = videoProvider === 'livekit';
  const callParticipantId = isMonitorView ? `${user.id}-monitor-${appointmentId}` : user.id;
  const activePerson = isDoctor ? patient : doctor;
  const auditActor = useMemo(() => ({
    id: user.id,
    role: user.role,
    name: user.name,
  }), [user.id, user.role, user.name]);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    let cancelled = false;

    auditFetch(`${backendUrl}/api/platform/settings/public`, {}, auditActor)
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('No se pudo cargar la configuracion de video'))))
      .then((data) => {
        if (cancelled) return;
        const providers = Array.isArray(data?.settings?.video?.providers) ? data.settings.video.providers : [];
        const activeProvider = providers.find((provider) => provider.id === data?.settings?.video?.activeProviderId)
          || providers.find((provider) => provider.enabled)
          || null;
        const runtimeKind = String(activeProvider?.kind || DEFAULT_VIDEO_PROVIDER).trim().toLowerCase();
        setVideoProvider(runtimeKind === 'livekit' ? 'livekit' : 'p2p');
        setVideoProviderId(activeProvider?.id || '');
        setRecordingSettings(data?.settings?.recording || { enabled: false, autoStartWithConsent: false, storageMode: 'local' });
        setVideoProviderReady(true);
      })
      .catch((error) => {
        console.error('Error loading platform video settings:', error);
        if (cancelled) return;
        setVideoProvider(DEFAULT_VIDEO_PROVIDER);
        setVideoProviderId('');
        setRecordingSettings({ enabled: false, autoStartWithConsent: false, storageMode: 'local' });
        setVideoProviderReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [auditActor, backendUrl]);

  const applyAdmissionContextToHcu = useCallback((admission) => {
    if (!admission) return;
    const admissionReason = String(admission.consultationReason || '').trim();
    const admissionNotes = String(admission.notes || '').trim();
    if (!admissionReason && !admissionNotes) return;

    setHcuForm((current) => {
      const currentReason = current.consultationReason?.description || '';
      const baseReason = appointment.reason || mockNotes[appointment.id]?.consultation || '';
      const shouldUseAdmissionReason = admissionReason && (!currentReason || currentReason === baseReason);
      const nextRecommendations = admissionNotes && !String(current.treatmentPlan?.recommendations || '').includes(admissionNotes)
        ? [current.treatmentPlan?.recommendations, `Admision: ${admissionNotes}`].filter(Boolean).join('\n')
        : current.treatmentPlan?.recommendations || '';

      return {
        ...current,
        consultationReason: {
          ...current.consultationReason,
          description: shouldUseAdmissionReason ? admissionReason : currentReason,
        },
        currentIllness: shouldUseAdmissionReason && (!current.currentIllness || current.currentIllness === baseReason)
          ? admissionReason
          : current.currentIllness,
        treatmentPlan: {
          ...current.treatmentPlan,
          recommendations: nextRecommendations,
        },
      };
    });
  }, [appointment.id, appointment.reason]);

  const applyPreTriageToHcu = useCallback((preTriage) => {
    if (!preTriage) return;
    setHcuForm((current) => {
      const currentReason = current.consultationReason?.description || '';
      const baseReason = appointment.reason || mockNotes[appointment.id]?.consultation || '';
      const symptoms = String(preTriage.mainSymptoms || '').trim();
      const evolution = String(preTriage.evolutionTime || '').trim();
      const history = String(preTriage.relevantHistory || '').trim();
      const alarmSigns = (preTriage.selectedAlarmSigns || []).map((sign) => sign.label).join('; ');
      const preTriageClinicalDetails = [history, alarmSigns ? `Signos de alarma referidos: ${alarmSigns}` : ''].filter(Boolean).join('\n');

      return {
        ...current,
        consultationReason: {
          ...current.consultationReason,
          description: symptoms && (!currentReason || currentReason === baseReason) ? symptoms : currentReason,
        },
        currentIllness: symptoms && (!current.currentIllness || current.currentIllness === baseReason)
          ? [symptoms, evolution ? `Evolucion: ${evolution}` : ''].filter(Boolean).join('\n')
          : current.currentIllness,
        personalHistory: {
          ...current.personalHistory,
          clinicalDetails: preTriageClinicalDetails && !String(current.personalHistory?.clinicalDetails || '').includes(preTriageClinicalDetails)
            ? [current.personalHistory?.clinicalDetails, preTriageClinicalDetails].filter(Boolean).join('\n')
            : current.personalHistory?.clinicalDetails || '',
        },
      };
    });
  }, [appointment.id, appointment.reason]);

  useEffect(() => {
    let cancelled = false;
    auditFetch(`${backendUrl}/api/admissions/virtual/${appointmentId}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.success) return;
        setAdmissionContext(data);
        applyAdmissionContextToHcu(data.admission);
      })
      .catch((error) => {
        console.error('Error loading admission context:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [appointmentId, auditActor, backendUrl, applyAdmissionContextToHcu]);

  useEffect(() => {
    let cancelled = false;
    auditFetch(`${backendUrl}/api/pretriage/${appointmentId}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.preTriage) return;
        setPreTriageContext(data.preTriage);
        applyPreTriageToHcu(data.preTriage);
      })
      .catch((error) => {
        console.error('Error loading pretriage context:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [appointmentId, auditActor, backendUrl, applyPreTriageToHcu]);

  useEffect(() => {
    if (!isDoctor || !patient.id) return undefined;

    let cancelled = false;
    Promise.all([
      auditFetch(`${backendUrl}/api/patients/${patient.id}`, {}, auditActor)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => data?.profile || null)
        .catch(() => null),
      auditFetch(`${backendUrl}/api/consultations/records/${patient.id}`, {}, auditActor)
        .then((res) => (res.ok ? res.json() : []))
        .catch(() => []),
    ]).then(([profile, records]) => {
      if (cancelled) return;
      setPatientProfile(profile);
      setPreviousRecords((Array.isArray(records) ? records : []).filter((record) => record.appointmentId !== appointmentId));
    });

    return () => {
      cancelled = true;
    };
  }, [appointmentId, auditActor, backendUrl, isDoctor, patient.id]);

  useEffect(() => {
    let cancelled = false;
    auditFetch(`${backendUrl}/api/consultations/chat/${appointmentId}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Chat not found'))))
      .then((data) => {
        if (!cancelled) {
          setChatMessages(Array.isArray(data.messages) ? data.messages : []);
        }
      })
      .catch((error) => {
        console.error('Error loading clinical chat:', error);
        if (!cancelled) setChatSaveStatus('No se pudo cargar el historial de chat.');
      });

    return () => {
      cancelled = true;
    };
  }, [appointmentId, auditActor, backendUrl]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams();
    params.set('appointmentId', appointmentId);
    if (appointment.patient?.id) params.set('patientId', appointment.patient.id);

    auditFetch(`${backendUrl}/api/clinical-files?${params.toString()}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : { files: [] }))
      .then((data) => {
        if (cancelled) return;
        const files = Array.isArray(data.files) ? data.files : [];
        setClinicalFileEvents(files.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
      })
      .catch((error) => {
        console.error('Error loading clinical file events:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [appointment.patient?.id, appointmentId, auditActor, backendUrl, clinicalFilesRefresh]);


  useEffect(() => {
    if (!chatMessagesRef.current) return;
    chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
  }, [chatMessages]);

  useEffect(() => {
    if (user.role !== 'patient') return undefined;

    let cancelled = false;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    auditFetch(`${backendUrl}/api/consents/telemedicine/${appointmentId}?patientId=${user.id}`, {}, auditActor)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Consent pending'))))
      .then((data) => {
        if (cancelled) return;
        const consent = data?.consent;
        if (consent?.status !== 'accepted' && consent?.accepted !== true) {
          alert('Debes aceptar el consentimiento informado digital antes de ingresar a la teleconsulta.');
          navigate('/portal/dashboard');
        }
      })
      .catch(() => {
        if (!cancelled) {
          alert('Debes aceptar el consentimiento informado digital antes de ingresar a la teleconsulta.');
          navigate('/portal/dashboard');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [appointmentId, auditActor, navigate, user.id, user.role]);

  const processIceCandidatesQueue = useCallback(async () => {
    if (!peerConnectionRef.current || iceCandidatesQueueRef.current.length === 0) return;
    const queuedCandidates = iceCandidatesQueueRef.current;
    iceCandidatesQueueRef.current = [];

    for (const candidate of queuedCandidates) {
      try {
        if (candidate) {
          await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        }
      } catch (e) {
        console.error('Error adding queued ICE candidate:', e);
      }
    }
  }, []);

  const detachTrackFromElement = useCallback((trackRef, elementRef) => {
    if (trackRef.current && elementRef.current) {
      trackRef.current.detach(elementRef.current);
    }
    if (elementRef.current) {
      elementRef.current.srcObject = null;
    }
    trackRef.current = null;
  }, []);

  const stopTrackWithElement = useCallback((trackRef, elementRef) => {
    if (trackRef.current && elementRef.current) {
      trackRef.current.detach(elementRef.current);
    }
    if (trackRef.current) {
      trackRef.current.stop();
    }
    if (elementRef.current) {
      elementRef.current.srcObject = null;
    }
    trackRef.current = null;
  }, []);

  const startLocalVideo = useCallback(async () => {
    if (isLiveKitProvider && localVideoTrackRef.current && localAudioTrackRef.current) {
      return {
        videoTrack: localVideoTrackRef.current,
        audioTrack: localAudioTrackRef.current,
      };
    }
    if (!isLiveKitProvider && streamRef.current) return streamRef.current;
    if (localMediaPromiseRef.current) return localMediaPromiseRef.current;

    localMediaPromiseRef.current = (async () => {
      try {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const isSecure = window.location.protocol === 'https:' || window.location.hostname === 'localhost';

        if (isMobile && !isSecure) {
          alert('Para usar la camara y microfono en dispositivos moviles, necesitas acceder desde HTTPS.');
          setConsultationStatus('error');
          localMediaPromiseRef.current = null;
          return null;
        }

        if (isLiveKitProvider) {
          const [videoTrack, audioTrack] = await Promise.all([
            createLocalVideoTrack({
              facingMode: isMobile ? 'user' : 'user',
            }),
            createLocalAudioTrack(),
          ]);

          localVideoTrackRef.current = videoTrack;
          localAudioTrackRef.current = audioTrack;
          streamRef.current = new MediaStream([
            videoTrack.mediaStreamTrack,
            audioTrack.mediaStreamTrack,
          ]);

          if (localVideoRef.current) {
            videoTrack.attach(localVideoRef.current);
            localVideoRef.current.muted = true;
          }
        } else {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: isMobile ? 'user' : 'user' },
            audio: true,
          });
          streamRef.current = stream;

          if (localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
          }
        }

        setConsultationStatus((currentStatus) => (
          currentStatus === 'connecting' ? 'ready' : currentStatus
        ));
        return isLiveKitProvider
          ? {
            videoTrack: localVideoTrackRef.current,
            audioTrack: localAudioTrackRef.current,
          }
          : streamRef.current;
      } catch (error) {
        localMediaPromiseRef.current = null;
        if (localVideoTrackRef.current) localVideoTrackRef.current.stop();
        if (localAudioTrackRef.current) localAudioTrackRef.current.stop();
        localVideoTrackRef.current = null;
        localAudioTrackRef.current = null;
        streamRef.current = null;
        console.error('Error accessing media devices:', error);
        alert('No se pudo acceder a la camara o microfono. Verifica permisos y dispositivos.');
        setConsultationStatus('error');
        return null;
      }
    })();

    return localMediaPromiseRef.current;
  }, [isLiveKitProvider]);

  const connectLiveKit = useCallback(async () => {
    if (liveKitRoomRef.current) return liveKitRoomRef.current;
    if (liveKitConnectPromiseRef.current) return liveKitConnectPromiseRef.current;

    const connectionPromise = (async () => {
    try {
      const localTracks = await startLocalVideo();
      if (!localTracks?.videoTrack || !localTracks?.audioTrack) return null;

      const response = await auditFetch(`${backendUrl}/api/livekit/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId,
          userId: callParticipantId,
          role: user.role,
          name: user.name,
          providerId: videoProviderId,
        }),
      }, auditActor);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'No se pudo obtener el token de LiveKit');
      }

      const tokenData = await response.json();
      const room = new Room();
      liveKitRoomRef.current = room;

      room
        .on(RoomEvent.ParticipantConnected, (participant) => {
          setOtherUser({
            id: participant.identity,
            name: participant.name || participant.identity,
            role: 'participant',
          });
          setConsultationStatus((currentStatus) => (
            currentStatus === 'active' ? currentStatus : 'ready'
          ));
        })
        .on(RoomEvent.ParticipantDisconnected, () => {
          detachTrackFromElement(remoteVideoTrackRef, remoteVideoRef);
          detachTrackFromElement(remoteAudioTrackRef, remoteAudioRef);
          setConsultationStatus((currentStatus) => (
            currentStatus === 'ended' ? currentStatus : 'reconnecting'
          ));
        })
        .on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
          setOtherUser({
            id: participant.identity,
            name: participant.name || participant.identity,
            role: 'participant',
          });

          if (track.kind === Track.Kind.Video && remoteVideoRef.current) {
            detachTrackFromElement(remoteVideoTrackRef, remoteVideoRef);
            remoteVideoTrackRef.current = track;
            track.attach(remoteVideoRef.current);
            setConsultationStatus('active');
          }

          if (track.kind === Track.Kind.Audio && remoteAudioRef.current) {
            detachTrackFromElement(remoteAudioTrackRef, remoteAudioRef);
            remoteAudioTrackRef.current = track;
            track.attach(remoteAudioRef.current);
          }
        })
        .on(RoomEvent.TrackUnsubscribed, (track) => {
          if (track.kind === Track.Kind.Video) {
            detachTrackFromElement(remoteVideoTrackRef, remoteVideoRef);
          }
          if (track.kind === Track.Kind.Audio) {
            detachTrackFromElement(remoteAudioTrackRef, remoteAudioRef);
          }
        })
        .on(RoomEvent.Reconnecting, () => {
          setConsultationStatus('reconnecting');
        })
        .on(RoomEvent.Reconnected, () => {
          setConsultationStatus(remoteVideoTrackRef.current ? 'active' : 'ready');
        })
        .on(RoomEvent.Disconnected, () => {
          detachTrackFromElement(remoteVideoTrackRef, remoteVideoRef);
          detachTrackFromElement(remoteAudioTrackRef, remoteAudioRef);
          liveKitRoomRef.current = null;
          setConsultationStatus((currentStatus) => (
            currentStatus === 'ended' ? currentStatus : 'reconnecting'
          ));
        });

      await room.connect(tokenData.livekitUrl, tokenData.token);
      await room.localParticipant.publishTrack(localTracks.videoTrack);
      await room.localParticipant.publishTrack(localTracks.audioTrack);

      room.remoteParticipants.forEach((participant) => {
        setOtherUser({
          id: participant.identity,
          name: participant.name || participant.identity,
          role: 'participant',
        });
      });

      setConsultationStatus(room.remoteParticipants.size > 0 ? 'active' : 'ready');
      return room;
    } catch (error) {
      console.error('LiveKit setup error:', error);
      alert(error.message || 'No se pudo conectar la sala LiveKit.');
      setConsultationStatus('error');
      return null;
    } finally {
      liveKitConnectPromiseRef.current = null;
    }
    })();

    liveKitConnectPromiseRef.current = connectionPromise;
    return connectionPromise;
  }, [appointmentId, auditActor, backendUrl, callParticipantId, detachTrackFromElement, startLocalVideo, user.name, user.role, videoProviderId]);

  const setupWebRTC = useCallback(async (shouldCreateOffer = user.role === 'doctor') => {
    try {
      if (isLiveKitProvider) {
        return connectLiveKit();
      }
      const activeSocket = socketRef.current;
      if (!activeSocket) return;

      const localStream = await startLocalVideo();
      if (!localStream) return;
      if (peerConnectionRef.current) return peerConnectionRef.current;

      const peerConnection = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' },
          { urls: 'turn:turn.bistri.com:80', username: 'homeo', credential: 'homeo' },
          { urls: 'turn:turn.anyfirewall.com:443?transport=tcp', username: 'webrtc', credential: 'webrtc' },
        ],
      });
      peerConnectionRef.current = peerConnection;

      localStream.getTracks().forEach(track => {
        peerConnection.addTrack(track, localStream);
      });

      peerConnection.ontrack = (event) => {
        if (remoteVideoRef.current) {
          const remoteStream = event.streams && event.streams[0] ? event.streams[0] : new MediaStream();
          if (event.track && !remoteStream.getTracks().some((track) => track.id === event.track.id)) {
            remoteStream.addTrack(event.track);
          }
          remoteVideoRef.current.srcObject = remoteStream;
          setConsultationStatus('active');
        }
      };

      peerConnection.onicecandidate = (event) => {
        const currentSocket = socketRef.current;
        if (event.candidate && currentSocket) {
          currentSocket.emit('webrtc:ice', { appointmentId, candidate: event.candidate });
        }
      };

      peerConnection.oniceconnectionstatechange = () => {
        console.log('ICE connection state:', peerConnection.iceConnectionState);
        if (['failed', 'disconnected'].includes(peerConnection.iceConnectionState)) {
          setConsultationStatus('reconnecting');
        }
      };

      peerConnection.onconnectionstatechange = () => {
        console.log('Peer connection state:', peerConnection.connectionState);
        if (peerConnection.connectionState === 'connected') {
          setConsultationStatus('active');
        }
        if (['failed', 'disconnected'].includes(peerConnection.connectionState)) {
          setConsultationStatus('reconnecting');
        }
        if (peerConnection.connectionState === 'closed') {
          setConsultationStatus('ended');
        }
      };

      if (shouldCreateOffer) {
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        activeSocket.emit('webrtc:offer', { appointmentId, offer });
      }
    } catch (error) {
      console.error('WebRTC setup error:', error);
    }
  }, [appointmentId, connectLiveKit, isLiveKitProvider, startLocalVideo, user.role]);

  useEffect(() => {
    if (isClinicalOnlyView) {
      setConsultationStatus('ready');
      return undefined;
    }
    if (!videoProviderReady) return undefined;
    const newSocket = io(process.env.REACT_APP_BACKEND_URL || undefined);
    socketRef.current = newSocket;
    setSocket(newSocket);

    const joinConsultation = async () => {
      if (hasJoinedConsultationRef.current) return;
      hasJoinedConsultationRef.current = true;
      const media = await startLocalVideo();
      if (!media || !newSocket.connected) {
        hasJoinedConsultationRef.current = false;
        return;
      }

      newSocket.emit('user:join', { userId: callParticipantId, role: user.role, appointmentId });
      if (isLiveKitProvider) {
        await connectLiveKit();
      }
    };

    newSocket.on('connect', joinConsultation);
    newSocket.on('user:joined', (data) => {
      setOtherUser(data);
      setConsultationStatus((currentStatus) => (
        currentStatus === 'active' ? currentStatus : 'ready'
      ));
    });
    newSocket.on('notes:updated', (data) => {
      if (data?.notes?.hcu002) {
        setHcuForm(data.notes.hcu002);
      }
    });
    newSocket.on('chat:message', (data) => {
      if (!data?.message?.id) return;
      if (data.message.author?.id !== user.id) {
        setUnreadChatCount((current) => current + 1);
      }
      setChatMessages((currentMessages) => (
        currentMessages.some((message) => message.id === data.message.id)
          ? currentMessages
          : [...currentMessages, data.message]
      ));
    });
    newSocket.on('clinical-file:uploaded', (data) => {
      setClinicalFilesRefresh((current) => current + 1);
      if (data?.file?.id) {
        setClinicalFileEvents((currentFiles) => (
          currentFiles.some((file) => file.id === data.file.id)
            ? currentFiles
            : [data.file, ...currentFiles]
        ));
      }
      if (data?.file?.uploadedBy !== user.id) {
        setLatestClinicalFileAlert(data.file);
        if (isDoctor) {
          setUnreadChatCount((current) => current + 1);
        }
      }
    });
    newSocket.on('admission:updated', (data) => {
      if (data?.appointmentId !== appointmentId) return;
      setAdmissionContext((current) => ({
        ...(current || {}),
        admission: data.admission,
      }));
      applyAdmissionContextToHcu(data.admission);
    });
    newSocket.on('pretriage:updated', (data) => {
      if (data?.appointmentId !== appointmentId) return;
      setPreTriageContext(data.preTriage);
      applyPreTriageToHcu(data.preTriage);
    });
    newSocket.on('consultation:status', (data) => {
      if (data?.status === 'ended') {
        setConsultationStatus('ended');
      }
    });
    newSocket.on('recording:started', (data) => {
      setRecordingState({
        status: 'recording',
        message: 'Grabando consulta',
        recordingId: data?.id || '',
      });
    });
    newSocket.on('recording:stopped', (data) => {
      setRecordingState({
        status: 'stopped',
        message: 'Grabacion finalizada',
        recordingId: data?.id || '',
      });
    });
    newSocket.on('recording:error', (data) => {
      setRecordingState({
        status: 'error',
        message: data?.message || 'Error de grabacion',
        recordingId: '',
      });
    });
    newSocket.on('user:left', () => {
      setConsultationStatus((currentStatus) => (
        currentStatus === 'ended' ? currentStatus : 'reconnecting'
      ));
    });
    newSocket.on('webrtc:offer', async (data) => {
      if (data.from === callParticipantId) return;
      if (!peerConnectionRef.current) await setupWebRTC(false);
      if (peerConnectionRef.current) {
        try {
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.offer));
          await processIceCandidatesQueue();
          const answer = await peerConnectionRef.current.createAnswer();
          await peerConnectionRef.current.setLocalDescription(answer);
          newSocket.emit('webrtc:answer', { appointmentId, answer });
        } catch (e) {
          console.error('Error handling offer:', e);
        }
      }
    });
    newSocket.on('webrtc:answer', async (data) => {
      if (data.from === callParticipantId) return;
      if (!peerConnectionRef.current) await setupWebRTC(false);
      if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription === null) {
        try {
          await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.answer));
          await processIceCandidatesQueue();
        } catch (e) {
          console.error('Error handling answer:', e);
        }
      }
    });
    newSocket.on('webrtc:ice', async (data) => {
      if (data.from === callParticipantId) return;
      if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
        try {
          if (data.candidate) {
            await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
          }
        } catch (e) {
          console.error('Error adding ICE candidate:', e);
        }
      } else {
        iceCandidatesQueueRef.current.push(data.candidate);
      }
    });

    joinConsultation();

    return () => {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (liveKitRoomRef.current) {
        liveKitRoomRef.current.disconnect();
        liveKitRoomRef.current = null;
      }
      liveKitConnectPromiseRef.current = null;
      detachTrackFromElement(remoteVideoTrackRef, remoteVideoRef);
      detachTrackFromElement(remoteAudioTrackRef, remoteAudioRef);
      stopTrackWithElement(localVideoTrackRef, localVideoRef);
      stopTrackWithElement(localAudioTrackRef, { current: null });
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
      socketRef.current = null;
      hasJoinedConsultationRef.current = false;
      localMediaPromiseRef.current = null;
      newSocket.disconnect();
    };
  }, [appointmentId, applyAdmissionContextToHcu, applyPreTriageToHcu, callParticipantId, connectLiveKit, detachTrackFromElement, isClinicalOnlyView, isDoctor, isLiveKitProvider, processIceCandidatesQueue, setupWebRTC, startLocalVideo, stopTrackWithElement, user.id, user.role, videoProviderReady]);

  useEffect(() => {
    if (isClinicalOnlyView) return undefined;
    if (isLiveKitProvider) return undefined;
    if (otherUser && streamRef.current && consultationStatus === 'ready') {
      setupWebRTC();
    }
    return undefined;
  }, [consultationStatus, isClinicalOnlyView, isLiveKitProvider, otherUser, setupWebRTC]);

  useEffect(() => {
    if (consultationStatus !== 'active') return undefined;
    const timer = setInterval(() => {
      setCallSeconds((prevSeconds) => {
        const newSeconds = prevSeconds + 1;
        if (newSeconds === 900 && user.role === 'doctor') {
          alert('Aviso: La consulta ha superado los 15 minutos.');
        }
        return newSeconds;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [consultationStatus, user.role]);

  const formatDuration = (seconds) => {
    const minutes = String(Math.floor(seconds / 60)).padStart(2, '0');
    const secs = String(seconds % 60).padStart(2, '0');
    return `${minutes}:${secs}`;
  };

  const getVideoSender = () => (
    peerConnectionRef.current
      ?.getSenders()
      .find((sender) => sender.track?.kind === 'video')
  );

  const stopScreenShare = async () => {
    if (isLiveKitProvider) {
      if (liveKitRoomRef.current && isScreenSharing) {
        await liveKitRoomRef.current.localParticipant.setScreenShareEnabled(false);
      }
      setIsScreenSharing(false);
      return;
    }

    const screenStream = screenStreamRef.current;
    if (screenStream) {
      screenStream.getTracks().forEach((track) => track.stop());
      screenStreamRef.current = null;
    }

    const cameraTrack = streamRef.current?.getVideoTracks()[0];
    const videoSender = getVideoSender();
    if (cameraTrack && videoSender) {
      await videoSender.replaceTrack(cameraTrack);
    }
    if (localVideoRef.current && streamRef.current) {
      localVideoRef.current.srcObject = streamRef.current;
    }
    setIsScreenSharing(false);
  };

  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      await stopScreenShare();
      return;
    }

    if (isLiveKitProvider) {
      try {
        if (!liveKitRoomRef.current) {
          await connectLiveKit();
        }
        await liveKitRoomRef.current?.localParticipant.setScreenShareEnabled(true);
        setIsScreenSharing(true);
      } catch (error) {
        if (error?.name !== 'NotAllowedError') {
          console.error('Error sharing screen with LiveKit:', error);
          alert('No se pudo compartir pantalla. Verifica permisos del navegador.');
        }
      }
      return;
    }

    if (!navigator.mediaDevices?.getDisplayMedia) {
      alert('Tu navegador no permite compartir pantalla desde esta pagina.');
      return;
    }

    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });
      const screenTrack = screenStream.getVideoTracks()[0];
      if (!screenTrack) return;

      const videoSender = getVideoSender();
      if (videoSender) {
        await videoSender.replaceTrack(screenTrack);
      }
      screenStreamRef.current = screenStream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = screenStream;
      }
      setIsScreenSharing(true);
      screenTrack.onended = () => {
        stopScreenShare().catch((error) => console.error('Error stopping screen share:', error));
      };
    } catch (error) {
      if (error?.name !== 'NotAllowedError') {
        console.error('Error sharing screen:', error);
        alert('No se pudo compartir pantalla. Verifica permisos del navegador.');
      }
    }
  };

  const toggleVideo = () => {
    if (isLiveKitProvider && localVideoTrackRef.current) {
      if (isVideoOn) {
        localVideoTrackRef.current.mute();
      } else {
        localVideoTrackRef.current.unmute();
      }
      setIsVideoOn(!isVideoOn);
      return;
    }

    if (streamRef.current) {
      streamRef.current.getVideoTracks().forEach(track => {
        track.enabled = !isVideoOn;
      });
      setIsVideoOn(!isVideoOn);
    }
  };

  const toggleAudio = () => {
    if (isLiveKitProvider && localAudioTrackRef.current) {
      if (isAudioOn) {
        localAudioTrackRef.current.mute();
      } else {
        localAudioTrackRef.current.unmute();
      }
      setIsAudioOn(!isAudioOn);
      return;
    }

    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !isAudioOn;
      });
      setIsAudioOn(!isAudioOn);
    }
  };

  const startRecording = useCallback(async (mode = 'manual') => {
    if (!isLiveKitProvider || !isDoctor || recordingState.status === 'recording' || recordingState.status === 'starting') return;
    setRecordingState({ status: 'starting', message: 'Iniciando grabacion...', recordingId: recordingState.recordingId || '' });
    try {
      const response = await auditFetch(`${backendUrl}/api/livekit/recordings/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointmentId, mode }),
      }, auditActor);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo iniciar la grabacion');
      setRecordingState({
        status: 'recording',
        message: data.alreadyRecording ? 'Grabacion ya activa' : 'Grabando consulta',
        recordingId: data.id || '',
      });
    } catch (error) {
      console.error('Error starting recording:', error);
      setRecordingState({ status: 'error', message: error.message || 'No se pudo iniciar la grabacion', recordingId: '' });
    }
  }, [appointmentId, auditActor, backendUrl, isDoctor, isLiveKitProvider, recordingState.recordingId, recordingState.status]);

  const stopRecording = useCallback(async () => {
    if (!isLiveKitProvider || !isDoctor || !['recording', 'starting'].includes(recordingState.status)) return;
    setRecordingState((current) => ({ ...current, status: 'stopping', message: 'Deteniendo grabacion...' }));
    try {
      const response = await auditFetch(`${backendUrl}/api/livekit/recordings/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointmentId }),
      }, auditActor);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'No se pudo detener la grabacion');
      setRecordingState({
        status: 'stopped',
        message: data.alreadyStopped ? 'Grabacion ya finalizada' : 'Grabacion finalizada',
        recordingId: data.id || recordingState.recordingId || '',
      });
    } catch (error) {
      console.error('Error stopping recording:', error);
      setRecordingState({ status: 'error', message: error.message || 'No se pudo detener la grabacion', recordingId: recordingState.recordingId || '' });
    }
  }, [appointmentId, auditActor, backendUrl, isDoctor, isLiveKitProvider, recordingState.recordingId, recordingState.status]);

  useEffect(() => {
    if (!isDoctor || !isLiveKitProvider || consultationStatus !== 'active') return;
    if (!recordingSettings.enabled || !recordingSettings.autoStartWithConsent) return;
    if (autoRecordingStartedRef.current) return;
    autoRecordingStartedRef.current = true;
    startRecording('auto');
  }, [consultationStatus, isDoctor, isLiveKitProvider, recordingSettings.autoStartWithConsent, recordingSettings.enabled, startRecording]);

  const sendChatMessage = async (event) => {
    event?.preventDefault();
    const text = chatText.trim();
    if (!text || isSendingChat) return;

    setIsSendingChat(true);
    setChatSaveStatus('');
    try {
      const response = await auditFetch(`${backendUrl}/api/consultations/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId,
          patientId: appointment.patient?.id,
          patientName: appointment.patient?.name,
          doctorId: appointment.doctor?.id || user.id,
          text,
          author: {
            id: user.id,
            role: user.role,
            name: user.name,
          },
        }),
      }, auditActor);

      if (!response.ok) throw new Error('No se pudo guardar el mensaje');
      const data = await response.json();
      const savedMessage = data.message;
      setChatMessages((currentMessages) => (
        currentMessages.some((message) => message.id === savedMessage.id)
          ? currentMessages
          : [...currentMessages, savedMessage]
      ));
      setChatText('');
      setUnreadChatCount(0);
      setChatSaveStatus('Mensaje guardado en la consulta.');
      if (socket) {
        socket.emit('chat:message', { appointmentId, message: savedMessage });
      }
    } catch (error) {
      console.error('Error sending clinical chat message:', error);
      setChatSaveStatus('No se pudo enviar el mensaje.');
    } finally {
      setIsSendingChat(false);
    }
  };

  const emitHcuUpdate = (nextForm) => {
    if (socket) {
      socket.emit('notes:update', {
        appointmentId,
        notes: { formType: FORM_TYPE, recordStatus: nextForm.recordStatus, hcu002: nextForm },
      });
    }
  };

  const setForm = (updater) => {
    setHcuForm((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      emitHcuUpdate(next);
      return next;
    });
  };

  const updateSection = (section, field, value) => {
    setForm((current) => ({
      ...current,
      [section]: { ...current[section], [field]: value },
    }));
  };

  const updateChecklist = (section, key, field, value, subgroup) => {
    setForm((current) => {
      const target = subgroup ? current[section][subgroup] : current[section].items || current[section];
      const updatedTarget = {
        ...target,
        [key]: { ...target[key], [field]: value },
      };

      if (subgroup) {
        return { ...current, [section]: { ...current[section], [subgroup]: updatedTarget } };
      }
      if (current[section].items) {
        return { ...current, [section]: { ...current[section], items: updatedTarget } };
      }
      return { ...current, [section]: updatedTarget };
    });
  };

  const updateVitalSign = (field, value) => {
    setForm((current) => {
      const nextVitalSigns = { ...current.vitalSigns, [field]: value };
      if (field === 'weight' || field === 'height') {
        nextVitalSigns.bmi = calculateBmi(nextVitalSigns.weight, nextVitalSigns.height);
      }
      return { ...current, vitalSigns: nextVitalSigns };
    });
  };

  const updateClinicalFollowUp = (field, value) => {
    setForm((current) => ({
      ...current,
      clinicalFollowUp: {
        ...current.clinicalFollowUp,
        [field]: value,
      },
    }));
  };

  const updateDiagnosis = (index, field, value) => {
    const nextValue = field === 'cie10Code' ? value.toUpperCase() : value;
    setForm((current) => ({
      ...current,
      diagnoses: current.diagnoses.map((diagnosis, currentIndex) => (
        currentIndex === index ? {
          ...diagnosis,
          [field]: nextValue,
          ...(field === 'cie10Code' && findCie10ByCode(nextValue) ? { description: findCie10ByCode(nextValue).description } : {}),
          ...(field === 'cie10Code' && findCie10ByCode(nextValue) ? { searchTerm: `${nextValue} - ${findCie10ByCode(nextValue).description}` } : {}),
        } : diagnosis
      )),
    }));
  };

  const selectCie10Diagnosis = (index, code) => {
    const diagnosis = findCie10ByCode(code);
    if (!diagnosis) return;
    setForm((current) => ({
      ...current,
      diagnoses: current.diagnoses.map((currentDiagnosis, currentIndex) => (
        currentIndex === index
          ? {
            ...currentDiagnosis,
            cie10Code: diagnosis.code,
            description: diagnosis.description,
            searchTerm: `${diagnosis.code} - ${diagnosis.description}`,
          }
          : currentDiagnosis
      )),
    }));
  };

  const setPrimaryDiagnosis = (index) => {
    setForm((current) => ({
      ...current,
      diagnoses: current.diagnoses.map((diagnosis, currentIndex) => ({
        ...diagnosis,
        classification: currentIndex === index ? 'primary' : 'secondary',
      })),
    }));
  };

  const addDiagnosisRow = () => {
    setForm((current) => {
      if (current.diagnoses.length >= 6) return current;
      return {
        ...current,
        diagnoses: [...current.diagnoses, createEmptyDiagnosis()],
      };
    });
  };

  const removeDiagnosisRow = (index) => {
    setForm((current) => {
      if (current.diagnoses.length === 1) {
        return {
          ...current,
          diagnoses: [{ ...createEmptyDiagnosis(), classification: 'primary' }],
        };
      }
      const nextDiagnoses = current.diagnoses.filter((_, currentIndex) => currentIndex !== index);
      if (!nextDiagnoses.some((diagnosis) => diagnosis.classification === 'primary')) {
        nextDiagnoses[0] = { ...nextDiagnoses[0], classification: 'primary' };
      }
      return {
        ...current,
        diagnoses: nextDiagnoses,
      };
    });
  };

  const toggleSection = (section) => {
    setOpenSections((current) => ({ ...current, [section]: !current[section] }));
  };

  const validateHcuForm = (form) => {
    const errors = [];
    HCU_002_MODULES.forEach((module) => {
      getModuleCompletion(form, module).pending.forEach((message) => {
        errors.push(`${module.code}. ${message}.`);
      });
    });

    const completedDiagnoses = form.diagnoses.filter(diagnosisHasContent);
    if (!completedDiagnoses.some((diagnosis) => diagnosis.classification === 'primary')) {
      errors.push('I. Debe existir un diagnostico principal.');
    }
    completedDiagnoses.forEach((diagnosis, index) => {
      if (!diagnosis.description.trim()) {
        errors.push(`Diagnostico ${index + 1}: descripcion requerida.`);
      }
      if (getDiagnosisCode(diagnosis).trim() && !isValidCie10Code(getDiagnosisCode(diagnosis))) {
        errors.push(`Diagnostico ${index + 1}: codigo CIE-10 invalido.`);
      }
    });
    return [...new Set(errors)];
  };

  const fetchSummaryResource = async (url, fallback) => {
    try {
      const response = await auditFetch(url, {}, auditActor);
      if (!response.ok) return fallback;
      return response.json();
    } catch (error) {
      console.error('Error loading summary resource:', error);
      return fallback;
    }
  };

  const buildAutomaticConsultationSummary = async (form) => {
    const patientId = appointment.patient?.id;
    const [prescriptionData, orderData, fileData] = await Promise.all([
      fetchSummaryResource(`${backendUrl}/api/prescriptions/appointment/${appointmentId}`, { prescriptions: [] }),
      fetchSummaryResource(`${backendUrl}/api/medical-orders/appointment/${appointmentId}`, { orders: [] }),
      fetchSummaryResource(`${backendUrl}/api/clinical-files?appointmentId=${appointmentId}&patientId=${patientId || ''}`, { files: [] }),
    ]);

    const completedDiagnoses = (form.diagnoses || []).filter(diagnosisHasContent);
    const primaryDiagnosis = completedDiagnoses.find((diagnosis) => diagnosis.classification === 'primary') || completedDiagnoses[0] || null;
    const prescriptions = Array.isArray(prescriptionData.prescriptions) ? prescriptionData.prescriptions : [];
    const orders = Array.isArray(orderData.orders) ? orderData.orders : [];
    const files = Array.isArray(fileData.files) ? fileData.files : [];
    const prescriptionWarnings = prescriptions
      .flatMap((prescription) => [
        prescription.generalWarnings,
        ...(prescription.medications || []).map((medication) => medication.warnings),
      ])
      .filter(Boolean);

    return {
      generatedAt: new Date().toISOString(),
      appointmentId,
      patient: {
        id: patientId || '',
        name: form.facilityPatientData?.fullName || appointment.patient?.name || '',
        documentId: form.facilityPatientData?.documentId || patientProfile?.documentId || '',
      },
      doctor: {
        id: user.id,
        name: user.name || doctor.name || '',
      },
      reason: form.consultationReason?.description || appointment.reason || 'Motivo no registrado',
      currentIllness: form.currentIllness || '',
      diagnosis: primaryDiagnosis
        ? {
          code: getDiagnosisCode(primaryDiagnosis),
          description: primaryDiagnosis.description,
          type: primaryDiagnosis.type,
        }
        : null,
      secondaryDiagnoses: completedDiagnoses
        .filter((diagnosis) => diagnosis !== primaryDiagnosis)
        .map((diagnosis) => ({
          code: getDiagnosisCode(diagnosis),
          description: diagnosis.description,
          type: diagnosis.type,
        })),
      treatment: form.treatmentPlan?.therapeutic || 'Tratamiento no registrado',
      recommendations: form.treatmentPlan?.recommendations || form.treatmentPlan?.educational || 'Recomendaciones no registradas',
      alarmSigns: prescriptionWarnings.length > 0
        ? prescriptionWarnings.join('; ')
        : form.treatmentPlan?.recommendations || 'Acudir a emergencia ante empeoramiento, dificultad respiratoria, dolor intenso, fiebre persistente o signos de alarma.',
      prescriptions: prescriptions.map((prescription) => ({
        id: prescription.id,
        verificationCode: prescription.verificationCode,
        createdAt: prescription.createdAt,
        medications: (prescription.medications || []).map((medication) => ({
          name: medication.name,
          dose: medication.dose,
          frequency: medication.frequency,
          duration: medication.duration,
          instructions: medication.instructions,
        })),
      })),
      orders: orders.map((order) => ({
        id: order.id,
        type: order.orderType,
        priority: order.priority,
        relatedDiagnosis: order.relatedDiagnosis,
        requestedStudies: order.requestedStudies,
      })),
      nextAppointment: form.clinicalFollowUp?.requiresNextAppointment ? {
        date: form.clinicalFollowUp.nextAppointmentDate,
        time: form.clinicalFollowUp.nextAppointmentTime,
        mode: form.clinicalFollowUp.nextAppointmentMode,
        reason: form.clinicalFollowUp.nextAppointmentReason,
        notes: form.clinicalFollowUp.nextAppointmentNotes,
      } : null,
      relevantFiles: files.map((file) => ({
        id: file.id,
        name: file.originalName,
        category: file.category,
        mimeType: file.mimeType,
        notes: file.notes,
        createdAt: file.createdAt,
      })),
    };
  };

  const buildRecordPayload = (form, status, summary = null) => ({
    appointmentId,
    doctorId: user.id,
    patientId: appointment.patient?.id,
    patientName: appointment.patient?.name,
    data: {
      formType: FORM_TYPE,
      recordStatus: status,
      evolution: form.clinicalFollowUp?.evolution || '',
      nextAppointment: form.clinicalFollowUp?.requiresNextAppointment ? {
        date: form.clinicalFollowUp.nextAppointmentDate,
        time: form.clinicalFollowUp.nextAppointmentTime,
        mode: form.clinicalFollowUp.nextAppointmentMode,
        reason: form.clinicalFollowUp.nextAppointmentReason,
        notes: form.clinicalFollowUp.nextAppointmentNotes,
      } : null,
      clinicalChat: {
        savedAt: new Date().toISOString(),
        messageCount: chatMessages.length,
        messages: chatMessages,
      },
      clinicalAlerts: {
        generatedAt: new Date().toISOString(),
        count: clinicalAlerts.length,
        alerts: clinicalAlerts,
      },
      consultationSummary: summary || consultationSummary,
      hcu002: { ...form, recordStatus: status },
    },
  });

  const saveMedicalRecord = async (status = 'final', options = {}) => {
    if (user.role !== 'doctor') return false;

    const nextForm = { ...hcuForm, recordStatus: status };
    const errors = status === 'final' ? validateHcuForm(nextForm) : [];
    setFormErrors(errors);
    setSaveMessage('');

    if (errors.length > 0) {
      if (!options.silent) {
        setSaveMessage('Revisa los campos obligatorios antes de guardar la historia clinica.');
      }
      return false;
    }

    setIsSaving(true);
    try {
      const nextSummary = status === 'final'
        ? await buildAutomaticConsultationSummary(nextForm)
        : consultationSummary;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await auditFetch(`${backendUrl}/api/consultations/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildRecordPayload(nextForm, status, nextSummary)),
      }, user);

      if (!response.ok) throw new Error('No se pudo guardar el registro medico');
      setForm(nextForm);
      if (nextSummary) setConsultationSummary(nextSummary);
      setSaveMessage(status === 'draft' ? 'Borrador guardado.' : 'Historia clinica guardada.');
      return true;
    } catch (error) {
      console.error('Error saving medical record:', error);
      setSaveMessage('Error al guardar el registro medico.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleEndConsultation = async () => {
    if (user.role === 'doctor') {
      const saved = await saveMedicalRecord('final', { silent: true });
      if (!saved) {
        setSaveMessage('Completa los campos obligatorios para finalizar la consulta.');
        alert('No se puede finalizar: completa los campos obligatorios de la HCU 002.');
        return;
      }
    }

    if (socket) {
      socket.emit('consultation:status', { appointmentId, status: 'ended', message: 'Consulta finalizada' });
    }
    if (user.role === 'doctor' && ['recording', 'starting'].includes(recordingState.status)) {
      await stopRecording();
    }
    await stopScreenShare();
    if (liveKitRoomRef.current) {
      liveKitRoomRef.current.disconnect();
      liveKitRoomRef.current = null;
    }
    detachTrackFromElement(remoteVideoTrackRef, remoteVideoRef);
    detachTrackFromElement(remoteAudioTrackRef, remoteAudioRef);
    stopTrackWithElement(localVideoTrackRef, localVideoRef);
    stopTrackWithElement(localAudioTrackRef, { current: null });
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    localMediaPromiseRef.current = null;
    setConsultationStatus('ended');

    if (user.role === 'doctor') {
      const patientName = appointment.patient?.name || 'Paciente';
      auditFetch((process.env.REACT_APP_BACKEND_URL || '') + '/api/consultations/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId,
          doctorId: user.id,
          patientId: appointment.patient?.id,
          patientName,
          duration: callSeconds,
        }),
      }, user).catch((e) => console.error('Error saving consultation:', e));
    }

    alert(`Tiempo total de llamada: ${formatDuration(callSeconds)}`);
    navigate(user.role === 'doctor' ? '/plataforma/dashboard' : '/portal/dashboard');
  };

  const otherUserName = appointment.doctor?.name === user.name
    ? appointment.patient?.name
    : appointment.doctor?.name;
  const patientName = appointment.patient?.name || 'Paciente';
  const doctorName = appointment.doctor?.name || 'Medico';
  const connectionLabel = CONNECTION_LABELS[consultationStatus] || 'Conectando';
  const connectionDetail = CONNECTION_DETAILS[consultationStatus] || CONNECTION_DETAILS.connecting;
  const chatTranscriptLabel = chatMessages.length === 1 ? '1 mensaje' : `${chatMessages.length} mensajes`;
  const clinicalFilesLabel = clinicalFileEvents.length === 1 ? '1 documento' : `${clinicalFileEvents.length} documentos`;
  const focusChatPanel = () => {
    setUnreadChatCount(0);
    chatPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const openClinicalFilesFromAlert = () => {
    setLatestClinicalFileAlert(null);
    setUnreadChatCount(0);
    setActiveSidePanel(isDoctor ? 'documents' : 'files');
  };

  const formatChatTime = (createdAt) => (
    createdAt
      ? new Date(createdAt).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })
      : ''
  );

  const formatClinicalFileTime = (createdAt) => (
    createdAt
      ? new Date(createdAt).toLocaleString('es-EC', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
      : 'Fecha pendiente'
  );

  const openSecondMonitorView = () => {
    const monitorUrl = `${window.location.origin}${location.pathname}?display=monitor`;
    window.open(
      monitorUrl,
      `telemed-monitor-${appointmentId}`,
      'popup=yes,width=980,height=900,left=80,top=80'
    );
    navigate(`${location.pathname}?display=clinical`, { replace: true, state: location.state });
  };

  const openFullConsultationView = () => {
    navigate(location.pathname, { replace: true, state: location.state });
  };

  const medicalPanelPatient = useMemo(() => ({ ...patient, ...(patientProfile || {}) }), [patient, patientProfile]);
  const patientAge = medicalPanelPatient.age || calculateAge(medicalPanelPatient.birthDate);
  const admission = admissionContext?.admission || appointment.admission || null;
  const admissionChecks = admission?.checks || {};
  const admissionReady = Boolean(admission?.readyForDoctor);
  const admissionFileCount = admission?.documents?.clinicalFileCount ?? admissionContext?.derivedChecks?.clinicalFileCount ?? 0;
  const preTriage = preTriageContext || appointment.preTriage || null;
  const allergySummary = medicalPanelPatient.allergies || hcuForm.personalHistory?.clinicalDetails || 'Sin alergias registradas';
  const medicationSummary = medicalPanelPatient.currentMedications || 'Sin medicamentos registrados';
  const personalHistorySummary = medicalPanelPatient.medicalHistory || hcuForm.personalHistory?.clinicalDetails || 'Sin antecedentes personales registrados';
  const familyHistorySummary = medicalPanelPatient.familyHistory || hcuForm.familyHistory?.clinicalDetails || 'Sin antecedentes familiares registrados';
  const clinicalAlerts = useMemo(() => generateClinicalAlerts({
    patient: medicalPanelPatient,
    preTriage,
    hcuForm,
  }), [medicalPanelPatient, preTriage, hcuForm]);

  const getRecordMainDiagnosis = (record) => {
    const diagnoses = record?.hcu002?.diagnoses || [];
    const primary = diagnoses.find((diagnosis) => diagnosis.classification === 'primary' && (diagnosis.description || diagnosis.cie10Code))
      || diagnoses.find((diagnosis) => diagnosis.description || diagnosis.cie10Code);
    return primary
      ? `${primary.cie10Code || ''} ${primary.description || ''}`.trim()
      : record?.diagnosis || 'Consulta previa';
  };
  const currentPrimaryDiagnosis = getRecordMainDiagnosis({ hcu002: hcuForm });
  const referralClinicalSummary = [
    hcuForm.consultationReason?.description ? `Motivo: ${hcuForm.consultationReason.description}` : '',
    hcuForm.currentIllness ? `Enfermedad actual: ${hcuForm.currentIllness}` : '',
    personalHistorySummary ? `Antecedentes: ${personalHistorySummary}` : '',
    hcuForm.treatmentPlan?.therapeutic ? `Tratamiento: ${hcuForm.treatmentPlan.therapeutic}` : '',
    hcuForm.clinicalFollowUp?.evolution ? `Evolucion: ${hcuForm.clinicalFollowUp.evolution}` : '',
  ].filter(Boolean).join('\n');

  const handleQuickAction = (action) => {
    const messages = {
      prescription: 'Se abrió el plan terapeutico para preparar una receta digital.',
      order: 'Se abrió el plan para registrar ordenes de laboratorio o imagen.',
      certificate: 'Se abrió el modulo para generar certificado medico.',
      referral: 'Se abrió evolución y próxima cita para documentar referencia o derivación.',
    };

    if (action === 'referral') {
      setOpenSections((current) => ({ ...current, followUp: true }));
    } else {
      setOpenSections((current) => ({ ...current, plan: true }));
    }
    if (action === 'prescription') {
      setActiveSidePanel('prescription');
      setTimeout(() => prescriptionPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
    if (action === 'order') {
      setActiveSidePanel('orders');
      setTimeout(() => medicalOrdersPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
    if (action === 'certificate') {
      setActiveSidePanel('certificates');
      setTimeout(() => medicalCertificatesPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
    if (action === 'referral') {
      setActiveSidePanel('referral');
      setTimeout(() => referralPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
    setQuickActionMessage(messages[action] || 'Accion rapida activada.');
    setTimeout(() => setQuickActionMessage(''), 3500);
  };

  const previewConsultationSummary = async () => {
    if (!isDoctor) return;
    setQuickActionMessage('Generando resumen automatico...');
    const previewSummary = await buildAutomaticConsultationSummary({ ...hcuForm, recordStatus: hcuForm.recordStatus });
    setConsultationSummary(previewSummary);
    setQuickActionMessage('Resumen automatico actualizado.');
    setTimeout(() => setQuickActionMessage(''), 3500);
  };

  const menuItems = [
    ['Inicio', '⌂'],
    ['Mis Pacientes', '●'],
    ['Agenda', '▣'],
    ['Historial', '↺'],
    ['Ajustes', '⚙'],
  ];

  const renderInput = (label, value, onChange, type = 'text', placeholder = '') => (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <input
        type={type}
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-9 w-full rounded-sm border border-slate-200 bg-white px-2 text-sm text-[#33465b] outline-none focus:border-[#2b6da4] focus:ring-1 focus:ring-[#2b6da4]"
      />
    </label>
  );

  const renderTextarea = (label, value, onChange, placeholder = '', rows = 3) => (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <textarea
        value={value || ''}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full resize-y rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm text-[#33465b] outline-none focus:border-[#2b6da4] focus:ring-1 focus:ring-[#2b6da4]"
      />
    </label>
  );

  const renderSection = (key, title, children) => (
    <section className="overflow-hidden rounded-sm border border-slate-200 bg-white">
      <button
        type="button"
        className="flex w-full items-center justify-between bg-[#f6f9fb] px-3 py-2 text-left text-sm font-bold text-[#33465b]"
        onClick={() => toggleSection(key)}
      >
        <span>{title}</span>
        <span className="text-lg leading-none text-slate-400">{openSections[key] ? '-' : '+'}</span>
      </button>
      {openSections[key] && <div className="space-y-3 p-3">{children}</div>}
    </section>
  );

  const renderChecklist = (section, options, items, subgroup) => (
    <div className="space-y-2">
      {options.map(([key, label]) => (
        <div key={key} className="rounded-sm border border-slate-200 bg-white p-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-[#33465b]">
            <input
              type="checkbox"
              checked={Boolean(items[key]?.checked)}
              onChange={(event) => updateChecklist(section, key, 'checked', event.target.checked, subgroup)}
              className="h-4 w-4 accent-[#236eaa]"
            />
            {label}
          </label>
          {items[key]?.checked && (
            <textarea
              value={items[key]?.description || ''}
              onChange={(event) => updateChecklist(section, key, 'description', event.target.value, subgroup)}
              placeholder={`Describir ${label.toLowerCase()}`}
              rows={2}
              className="mt-2 w-full resize-y rounded-sm border border-slate-200 px-2 py-1.5 text-sm outline-none focus:border-[#2b6da4] focus:ring-1 focus:ring-[#2b6da4]"
            />
          )}
        </div>
      ))}
    </div>
  );

  const selectedSummary = (items, options) => options
    .filter(([key]) => items[key]?.checked)
    .map(([key, label]) => `${label}${items[key]?.description ? `: ${items[key].description}` : ''}`);

  const completedDiagnoses = hcuForm.diagnoses.filter(diagnosisHasContent);
  const formCompletion = getFormCompletionSummary(hcuForm);
  const sidePanels = isDoctor ? [
    ['admission', 'Admision', admissionReady ? 'Lista' : 'Pendiente'],
    ['patient', 'Paciente', clinicalAlerts.length ? `${clinicalAlerts.length} alerta(s)` : 'Ficha'],
    ['summary', 'Resumen', consultationSummary ? 'Generado' : 'Borrador'],
    ['documents', 'Archivos', 'Clinicos'],
    ['prescription', 'Receta', 'Rx'],
    ['orders', 'Ordenes', 'Lab/Img'],
    ['certificates', 'Certificados', 'Medico'],
    ['referral', 'Referencia', 'HCU 053'],
    ['hcu', 'HCU 002', `${formCompletion.percentage}%`],
  ] : [
    ['files', 'Archivos', 'Paciente'],
    ['hcu', 'Resumen', 'Clinico'],
  ];

  const escapePrint = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  }[char]));

  const printChecklist = (items, options) => {
    const selected = selectedSummary(items, options);
    return selected.length ? selected.map(escapePrint).join('<br />') : 'Sin patologias marcadas';
  };

  const buildPrintRows = (rows) => rows.map(([label, value]) => `
    <div class="cell"><strong>${escapePrint(label)}</strong><span>${escapePrint(value) || '&nbsp;'}</span></div>
  `).join('');

  const printHcuForm = () => {
    const diagnosesRows = Array.from({ length: 6 }, (_, index) => {
      const diagnosis = hcuForm.diagnoses[index] || createEmptyDiagnosis();
      return `
        <tr>
          <td>${index + 1}</td>
          <td>${escapePrint(diagnosis.description)}</td>
          <td>${escapePrint(getDiagnosisCode(diagnosis))}</td>
          <td>${diagnosis.classification === 'primary' ? 'X' : ''}</td>
          <td>${diagnosis.classification !== 'primary' ? 'X' : ''}</td>
          <td>${diagnosis.type === 'presuntivo' ? 'X' : ''}</td>
          <td>${diagnosis.type === 'definitivo' ? 'X' : ''}</td>
          <td>${escapePrint(diagnosis.observations)}</td>
        </tr>
      `;
    }).join('');

    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (!printWindow) {
      alert('Activa las ventanas emergentes para imprimir el formulario.');
      return;
    }

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>HCU 002 Consulta Externa</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; color: #111; margin: 0; background: #fff; }
            .page { width: 297mm; min-height: 210mm; padding: 9mm; margin: 0 auto; page-break-after: always; }
            .title { display: flex; justify-content: space-between; align-items: center; border: 2px solid #111; padding: 6px 8px; margin-bottom: 6px; }
            .title h1 { font-size: 15px; margin: 0; text-transform: uppercase; }
            .title span { font-size: 11px; font-weight: bold; }
            .section { border: 1.5px solid #111; margin-bottom: 6px; }
            .section h2 { margin: 0; padding: 4px 6px; background: #e9eef2; border-bottom: 1px solid #111; font-size: 11px; text-transform: uppercase; }
            .grid { display: grid; grid-template-columns: repeat(4, 1fr); }
            .grid.two { grid-template-columns: repeat(2, 1fr); }
            .grid.three { grid-template-columns: repeat(3, 1fr); }
            .cell { min-height: 34px; border-right: 1px solid #111; border-bottom: 1px solid #111; padding: 3px 5px; font-size: 10px; }
            .cell strong { display: block; font-size: 8px; text-transform: uppercase; margin-bottom: 3px; }
            .cell span { white-space: pre-wrap; }
            .text-box { min-height: 54px; padding: 6px; font-size: 10px; white-space: pre-wrap; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            th, td { border: 1px solid #111; padding: 4px; vertical-align: top; }
            th { background: #e9eef2; font-size: 9px; text-transform: uppercase; }
            .footer { display: flex; justify-content: space-between; font-size: 9px; margin-top: 8px; }
            @media print {
              @page { size: A4 landscape; margin: 0; }
              .page { margin: 0; }
            }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="title">
              <h1>SNS-MSP / HCU form.002 / Consulta Externa - Anamnesis</h1>
              <span>Hoja ${escapePrint(hcuForm.facilityPatientData.sheetNumber || '1')}</span>
            </div>
            <div class="section">
              <h2>A. Datos del establecimiento y usuario / paciente</h2>
              <div class="grid">
                ${buildPrintRows([
                  ['Institucion del sistema', hcuForm.facilityPatientData.institution],
                  ['Unicodigo', hcuForm.facilityPatientData.unicodigo],
                  ['Establecimiento de salud', hcuForm.facilityPatientData.healthFacility],
                  ['Historia clinica unica', hcuForm.facilityPatientData.clinicalHistoryNumber],
                  ['Documento identificacion', hcuForm.facilityPatientData.documentId],
                  ['Numero archivo', hcuForm.facilityPatientData.fileNumber],
                  ['Nombre completo', hcuForm.facilityPatientData.fullName],
                  ['Sexo / edad', `${hcuForm.facilityPatientData.sex} / ${hcuForm.facilityPatientData.age}`],
                ])}
              </div>
            </div>
            <div class="section"><h2>B. Motivo de consulta</h2><div class="text-box">Tipo: ${escapePrint(hcuForm.consultationReason.type)}\n${escapePrint(hcuForm.consultationReason.description)}</div></div>
            <div class="section">
              <h2>C-D. Antecedentes patologicos personales y familiares</h2>
              <div class="grid two">
                <div class="cell"><strong>Personales</strong><span>${printChecklist(hcuForm.personalHistory.items, historyOptions)}<br />${escapePrint(hcuForm.personalHistory.clinicalDetails)}</span></div>
                <div class="cell"><strong>Familiares</strong><span>${printChecklist(hcuForm.familyHistory.items, historyOptions)}<br />${escapePrint(hcuForm.familyHistory.clinicalDetails)}</span></div>
              </div>
            </div>
            <div class="section"><h2>E. Enfermedad o problema actual</h2><div class="text-box">${escapePrint(hcuForm.currentIllness)}</div></div>
            <div class="section">
              <h2>F. Constantes vitales y antropometria</h2>
              <div class="grid">
                ${buildPrintRows([
                  ['Fecha / hora', `${hcuForm.vitalSigns.date} ${hcuForm.vitalSigns.time}`],
                  ['Temperatura', hcuForm.vitalSigns.temperature],
                  ['Presion arterial', hcuForm.vitalSigns.bloodPressure],
                  ['Pulso', hcuForm.vitalSigns.pulse],
                  ['Frec. respiratoria', hcuForm.vitalSigns.respiratoryRate],
                  ['Peso / talla', `${hcuForm.vitalSigns.weight} / ${hcuForm.vitalSigns.height}`],
                  ['IMC', hcuForm.vitalSigns.bmi],
                  ['Pulsioximetria', hcuForm.vitalSigns.pulseOximetry],
                ])}
              </div>
            </div>
            <div class="section"><h2>G. Revision actual de organos y sistemas</h2><div class="text-box">${printChecklist(hcuForm.organSystemsReview, organSystemOptions)}</div></div>
            <div class="footer"><span>SNS-MSP / HCU-form.002 / 2021</span><span>CONSULTA EXTERNA - ANAMNESIS (1)</span></div>
          </div>
          <div class="page">
            <div class="title"><h1>SNS-MSP / HCU form.002 / Consulta Externa - Examen fisico y prescripciones</h1><span>Reverso</span></div>
            <div class="section">
              <h2>H. Examen fisico</h2>
              <div class="grid two">
                <div class="cell"><strong>Regional</strong><span>${printChecklist(hcuForm.physicalExam.regional, regionalExamOptions)}</span></div>
                <div class="cell"><strong>Sistemico</strong><span>${printChecklist(hcuForm.physicalExam.systemic, systemicExamOptions)}</span></div>
              </div>
            </div>
            <div class="section">
              <h2>I. Diagnostico CIE-10</h2>
              <table>
                <thead><tr><th>No.</th><th>Diagnostico</th><th>CIE-10</th><th>Principal</th><th>Sec.</th><th>PRE</th><th>DEF</th><th>Observaciones</th></tr></thead>
                <tbody>${diagnosesRows}</tbody>
              </table>
            </div>
            <div class="section">
              <h2>J. Plan de tratamiento diagnostico, terapeutico y educacional</h2>
              <div class="grid two">
                <div class="cell"><strong>Diagnostico</strong><span>${escapePrint(hcuForm.treatmentPlan.diagnostic)}</span></div>
                <div class="cell"><strong>Terapeutico</strong><span>${escapePrint(hcuForm.treatmentPlan.therapeutic)}</span></div>
                <div class="cell"><strong>Educacional</strong><span>${escapePrint(hcuForm.treatmentPlan.educational)}</span></div>
                <div class="cell"><strong>Recomendaciones</strong><span>${escapePrint(hcuForm.treatmentPlan.recommendations)}</span></div>
              </div>
            </div>
            <div class="section">
              <h2>L. Evolucion y proxima cita</h2>
              <div class="grid two">
                <div class="cell"><strong>Evolucion</strong><span>${escapePrint(hcuForm.clinicalFollowUp?.evolution)}</span></div>
                <div class="cell"><strong>Proxima cita</strong><span>${
                  hcuForm.clinicalFollowUp?.requiresNextAppointment
                    ? `${escapePrint(hcuForm.clinicalFollowUp.nextAppointmentDate)} ${escapePrint(hcuForm.clinicalFollowUp.nextAppointmentTime)} - ${escapePrint(hcuForm.clinicalFollowUp.nextAppointmentReason)} (${escapePrint(hcuForm.clinicalFollowUp.nextAppointmentMode)})`
                    : 'No requerida'
                }<br />${escapePrint(hcuForm.clinicalFollowUp?.nextAppointmentNotes)}</span></div>
              </div>
            </div>
            <div class="section">
              <h2>K. Datos del profesional responsable</h2>
              <div class="grid three">
                ${buildPrintRows([
                  ['Fecha', hcuForm.responsibleProfessional.date],
                  ['Hora', hcuForm.responsibleProfessional.time],
                  ['Nombre', hcuForm.responsibleProfessional.name],
                  ['Documento', hcuForm.responsibleProfessional.documentId],
                  ['Firma', hcuForm.responsibleProfessional.signature],
                  ['Sello', hcuForm.responsibleProfessional.stamp],
                ])}
              </div>
            </div>
            <div class="footer"><span>SNS-MSP / HCU-form.002 / 2021</span><span>CONSULTA EXTERNA - EXAMEN FISICO Y PRESCRIPCIONES (2)</span></div>
          </div>
          <script>window.onload = function(){ window.focus(); window.print(); };</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const renderPatientSummary = () => (
    <div className="space-y-3 text-sm text-[#33465b]">
      {[
        ['Motivo', hcuForm.consultationReason.description || 'En espera de registro medico.'],
        ['Enfermedad actual', hcuForm.currentIllness || 'En espera de evaluacion.'],
        ['Plan terapeutico', hcuForm.treatmentPlan.therapeutic || 'En espera de indicaciones.'],
        ['Recomendaciones', hcuForm.treatmentPlan.recommendations || 'En espera de recomendaciones.'],
        ['Evolucion', hcuForm.clinicalFollowUp?.evolution || 'En espera de cierre medico.'],
        ['Proxima cita', hcuForm.clinicalFollowUp?.requiresNextAppointment
          ? `${hcuForm.clinicalFollowUp.nextAppointmentDate || 'Fecha pendiente'} ${hcuForm.clinicalFollowUp.nextAppointmentTime || ''} - ${hcuForm.clinicalFollowUp.nextAppointmentReason || 'Motivo pendiente'}`
          : 'No registrada.'],
      ].map(([label, value]) => (
        <div key={label} className="rounded-sm border border-slate-200 bg-white px-3 py-2">
          <strong className="block text-xs uppercase text-slate-500">{label}</strong>
          <span>{value}</span>
        </div>
      ))}

      <div className="rounded-sm border border-slate-200 bg-white px-3 py-2">
        <strong className="block text-xs uppercase text-slate-500">Diagnosticos</strong>
        {completedDiagnoses.length === 0 ? (
          <span>En espera de diagnostico.</span>
        ) : (
          <ul className="mt-1 space-y-1">
            {completedDiagnoses.map((diagnosis, index) => (
              <li key={`${diagnosis.description}-${index}`}>
                {diagnosis.classification === 'primary' ? 'Principal: ' : 'Secundario: '}
                {diagnosis.description} {getDiagnosisCode(diagnosis) ? `(${getDiagnosisCode(diagnosis)})` : ''} - {diagnosis.type}
                {diagnosis.observations ? ` · ${diagnosis.observations}` : ''}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  const renderHcuForm = () => (
    <div className="space-y-3">
      {renderSection('patient', 'A. Datos del establecimiento y paciente', (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {renderInput('Institucion', hcuForm.facilityPatientData.institution, (value) => updateSection('facilityPatientData', 'institution', value))}
          {renderInput('Unicodigo', hcuForm.facilityPatientData.unicodigo, (value) => updateSection('facilityPatientData', 'unicodigo', value))}
          {renderInput('Establecimiento', hcuForm.facilityPatientData.healthFacility, (value) => updateSection('facilityPatientData', 'healthFacility', value))}
          {renderInput('Historia clinica unica', hcuForm.facilityPatientData.clinicalHistoryNumber, (value) => updateSection('facilityPatientData', 'clinicalHistoryNumber', value))}
          {renderInput('Documento identificacion', hcuForm.facilityPatientData.documentId, (value) => updateSection('facilityPatientData', 'documentId', value))}
          {renderInput('Nombre completo', hcuForm.facilityPatientData.fullName, (value) => updateSection('facilityPatientData', 'fullName', value))}
          {renderInput('Sexo', hcuForm.facilityPatientData.sex, (value) => updateSection('facilityPatientData', 'sex', value))}
          {renderInput('Edad', hcuForm.facilityPatientData.age, (value) => updateSection('facilityPatientData', 'age', value))}
        </div>
      ))}

      {renderSection('reason', 'B. Motivo y E. enfermedad actual', (
        <>
          <div className="grid grid-cols-2 gap-2">
            {['primera', 'subsecuente'].map((type) => (
              <label key={type} className="flex items-center gap-2 rounded-sm border border-slate-200 px-3 py-2 text-sm capitalize">
                <input
                  type="radio"
                  checked={hcuForm.consultationReason.type === type}
                  onChange={() => updateSection('consultationReason', 'type', type)}
                  className="accent-[#236eaa]"
                />
                {type}
              </label>
            ))}
          </div>
          {renderTextarea('Motivo de consulta', hcuForm.consultationReason.description, (value) => updateSection('consultationReason', 'description', value), 'Registrar motivo principal')}
          {renderTextarea('Enfermedad o problema actual', hcuForm.currentIllness, (value) => setForm((current) => ({ ...current, currentIllness: value })), 'Cronologia, localizacion, caracteristicas, intensidad, frecuencia y factores agravantes', 4)}
        </>
      ))}

      {renderSection('history', 'C-D. Antecedentes', (
        <>
          <p className="text-xs font-bold uppercase text-slate-500">Personales</p>
          {renderChecklist('personalHistory', historyOptions, hcuForm.personalHistory.items)}
          {renderTextarea('Datos clinico-quirurgicos, obstetricos, alergicos relevantes', hcuForm.personalHistory.clinicalDetails, (value) => updateSection('personalHistory', 'clinicalDetails', value))}
          <p className="pt-2 text-xs font-bold uppercase text-slate-500">Familiares</p>
          {renderChecklist('familyHistory', historyOptions, hcuForm.familyHistory.items)}
          {renderTextarea('Detalle familiar relevante', hcuForm.familyHistory.clinicalDetails, (value) => updateSection('familyHistory', 'clinicalDetails', value))}
        </>
      ))}

      {renderSection('vitals', 'F. Constantes vitales y antropometria', (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {renderInput('Fecha', hcuForm.vitalSigns.date, (value) => updateVitalSign('date', value), 'date')}
          {renderInput('Hora', hcuForm.vitalSigns.time, (value) => updateVitalSign('time', value), 'time')}
          {renderInput('Temperatura C', hcuForm.vitalSigns.temperature, (value) => updateVitalSign('temperature', value))}
          {renderInput('Presion arterial', hcuForm.vitalSigns.bloodPressure, (value) => updateVitalSign('bloodPressure', value), 'text', '120/80')}
          {renderInput('Pulso / min', hcuForm.vitalSigns.pulse, (value) => updateVitalSign('pulse', value))}
          {renderInput('Frecuencia respiratoria / min', hcuForm.vitalSigns.respiratoryRate, (value) => updateVitalSign('respiratoryRate', value))}
          {renderInput('Peso Kg', hcuForm.vitalSigns.weight, (value) => updateVitalSign('weight', value))}
          {renderInput('Talla cm o m', hcuForm.vitalSigns.height, (value) => updateVitalSign('height', value))}
          {renderInput('IMC Kg/m2', hcuForm.vitalSigns.bmi, () => {}, 'text')}
          {renderInput('Perimetro abdominal cm', hcuForm.vitalSigns.abdominalPerimeter, (value) => updateVitalSign('abdominalPerimeter', value))}
          {renderInput('Hemoglobina capilar g/dl', hcuForm.vitalSigns.capillaryHemoglobin, (value) => updateVitalSign('capillaryHemoglobin', value))}
          {renderInput('Glucosa capilar mg/dl', hcuForm.vitalSigns.capillaryGlucose, (value) => updateVitalSign('capillaryGlucose', value))}
          {renderInput('Pulsioximetria %', hcuForm.vitalSigns.pulseOximetry, (value) => updateVitalSign('pulseOximetry', value))}
        </div>
      ))}

      {renderSection('systems', 'G. Revision actual de organos y sistemas', (
        renderChecklist('organSystemsReview', organSystemOptions, hcuForm.organSystemsReview)
      ))}

      {renderSection('exam', 'H. Examen fisico', (
        <>
          <p className="text-xs font-bold uppercase text-slate-500">Regional</p>
          {renderChecklist('physicalExam', regionalExamOptions, hcuForm.physicalExam.regional, 'regional')}
          <p className="pt-2 text-xs font-bold uppercase text-slate-500">Sistemico</p>
          {renderChecklist('physicalExam', systemicExamOptions, hcuForm.physicalExam.systemic, 'systemic')}
        </>
      ))}

      {renderSection('diagnosis', 'I. Diagnostico CIE-10', (
        <div className="space-y-3">
          <div className="rounded-sm border border-[#cfe0ee] bg-[#f6f9fb] px-3 py-2 text-xs text-[#405165]">
            Busca por codigo o descripcion CIE-10. Marca un diagnostico principal y agrega secundarios con observaciones clinicas si aplica.
          </div>
          {hcuForm.diagnoses.map((diagnosis, index) => (
            <div key={index} className="rounded-sm border border-slate-200 p-2">
              <div className="mb-2 flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-bold text-slate-500">Diagnostico {index + 1}</p>
                  <p className="text-[11px] font-semibold text-[#236eaa]">
                    {diagnosis.classification === 'primary' ? 'Principal' : 'Secundario'}
                    {getDiagnosisCode(diagnosis) ? ` · ${getDiagnosisCode(diagnosis)}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded-sm border border-red-200 px-2 py-1 text-xs font-bold text-red-600 hover:bg-red-50"
                  onClick={() => removeDiagnosisRow(index)}
                >
                  Quitar
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_135px_120px]">
                <input
                  value={diagnosis.searchTerm || ''}
                  onChange={(event) => updateDiagnosis(index, 'searchTerm', event.target.value)}
                  placeholder="Buscar CIE-10 por codigo o descripcion"
                  className="h-9 rounded-sm border border-slate-200 px-2 text-sm outline-none focus:border-[#2b6da4]"
                />
                <button
                  type="button"
                  onClick={() => setPrimaryDiagnosis(index)}
                  className={`h-9 rounded-sm border px-2 text-xs font-bold ${diagnosis.classification === 'primary' ? 'border-[#236eaa] bg-[#edf5fb] text-[#236eaa]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                >
                  Principal
                </button>
                <select value={diagnosis.type} onChange={(event) => updateDiagnosis(index, 'type', event.target.value)} className="h-9 rounded-sm border border-slate-200 px-2 text-sm outline-none focus:border-[#2b6da4]">
                  <option value="presuntivo">Presuntivo</option>
                  <option value="definitivo">Definitivo</option>
                </select>
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[120px_1fr]">
                <input
                  value={getDiagnosisCode(diagnosis)}
                  onChange={(event) => updateDiagnosis(index, 'cie10Code', event.target.value)}
                  onBlur={(event) => {
                    const code = event.target.value.toUpperCase();
                    if (findCie10ByCode(code)) selectCie10Diagnosis(index, code);
                  }}
                  placeholder="Codigo"
                  className="h-9 rounded-sm border border-slate-200 px-2 text-sm uppercase outline-none focus:border-[#2b6da4]"
                />
                <input
                  value={diagnosis.description}
                  onChange={(event) => updateDiagnosis(index, 'description', event.target.value)}
                  placeholder="Descripcion diagnostica"
                  className="h-9 rounded-sm border border-slate-200 px-2 text-sm outline-none focus:border-[#2b6da4]"
                />
              </div>
              <div className="mt-2 grid gap-1">
                {searchCie10Catalog(diagnosis.searchTerm || getDiagnosisCode(diagnosis) || diagnosis.description).map((item) => (
                  <button
                    type="button"
                    key={item.code}
                    onClick={() => selectCie10Diagnosis(index, item.code)}
                    className={`rounded-sm border px-2 py-1.5 text-left text-xs hover:border-[#236eaa] hover:bg-[#edf5fb] ${getDiagnosisCode(diagnosis) === item.code ? 'border-[#236eaa] bg-[#edf5fb] text-[#236eaa]' : 'border-slate-200 text-[#405165]'}`}
                  >
                    <strong>{item.code}</strong> · {item.description}
                  </button>
                ))}
              </div>
              {renderTextarea('Observaciones del diagnostico', diagnosis.observations, (value) => updateDiagnosis(index, 'observations', value), 'Severidad, lateralidad, relacion con antecedentes, criterio clinico o notas para seguimiento', 2)}
            </div>
          ))}
          <button
            type="button"
            className="w-full rounded-sm border border-dashed border-[#236eaa] px-3 py-2 text-sm font-bold text-[#236eaa] hover:bg-[#edf5fb] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={addDiagnosisRow}
            disabled={hcuForm.diagnoses.length >= 6}
          >
            + Agregar diagnostico CIE-10
          </button>
          {hcuForm.diagnoses.length >= 6 && (
            <p className="text-xs text-slate-500">El formulario HCU 002 permite hasta 6 diagnosticos en esta seccion.</p>
          )}
        </div>
      ))}

      {renderSection('plan', 'J-K. Plan de tratamiento y responsable', (
        <>
          {renderTextarea('Plan diagnostico', hcuForm.treatmentPlan.diagnostic, (value) => updateSection('treatmentPlan', 'diagnostic', value))}
          {renderTextarea('Plan terapeutico', hcuForm.treatmentPlan.therapeutic, (value) => updateSection('treatmentPlan', 'therapeutic', value))}
          {renderTextarea('Indicaciones educativas', hcuForm.treatmentPlan.educational, (value) => updateSection('treatmentPlan', 'educational', value))}
          {renderTextarea('Recomendaciones', hcuForm.treatmentPlan.recommendations, (value) => updateSection('treatmentPlan', 'recommendations', value))}
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {renderInput('Fecha profesional', hcuForm.responsibleProfessional.date, (value) => updateSection('responsibleProfessional', 'date', value), 'date')}
            {renderInput('Hora profesional', hcuForm.responsibleProfessional.time, (value) => updateSection('responsibleProfessional', 'time', value), 'time')}
            {renderInput('Profesional responsable', hcuForm.responsibleProfessional.name, (value) => updateSection('responsibleProfessional', 'name', value))}
            {renderInput('Documento profesional', hcuForm.responsibleProfessional.documentId, (value) => updateSection('responsibleProfessional', 'documentId', value))}
            {renderInput('Firma', hcuForm.responsibleProfessional.signature, (value) => updateSection('responsibleProfessional', 'signature', value))}
            {renderInput('Sello', hcuForm.responsibleProfessional.stamp, (value) => updateSection('responsibleProfessional', 'stamp', value))}
          </div>
        </>
      ))}

      {renderSection('followUp', 'L. Evolucion y proxima cita', (
        <>
          {renderTextarea('Evolucion de la consulta', hcuForm.clinicalFollowUp?.evolution, (value) => updateClinicalFollowUp('evolution', value), 'Respuesta durante la atencion, impresion evolutiva, condicion al cierre y conducta final', 4)}
          <label className="flex items-center gap-2 rounded-sm border border-slate-200 bg-[#f6f9fb] px-3 py-2 text-sm font-semibold text-[#33465b]">
            <input
              type="checkbox"
              checked={Boolean(hcuForm.clinicalFollowUp?.requiresNextAppointment)}
              onChange={(event) => updateClinicalFollowUp('requiresNextAppointment', event.target.checked)}
              className="h-4 w-4 accent-[#236eaa]"
            />
            Requiere proxima cita o control
          </label>
          {hcuForm.clinicalFollowUp?.requiresNextAppointment && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {renderInput('Fecha proxima cita', hcuForm.clinicalFollowUp.nextAppointmentDate, (value) => updateClinicalFollowUp('nextAppointmentDate', value), 'date')}
              {renderInput('Hora proxima cita', hcuForm.clinicalFollowUp.nextAppointmentTime, (value) => updateClinicalFollowUp('nextAppointmentTime', value), 'time')}
              <label className="block">
                <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Modalidad</span>
                <select
                  value={hcuForm.clinicalFollowUp.nextAppointmentMode || 'telemedicina'}
                  onChange={(event) => updateClinicalFollowUp('nextAppointmentMode', event.target.value)}
                  className="h-9 w-full rounded-sm border border-slate-200 bg-white px-2 text-sm text-[#33465b] outline-none focus:border-[#2b6da4] focus:ring-1 focus:ring-[#2b6da4]"
                >
                  <option value="telemedicina">Telemedicina</option>
                  <option value="presencial">Presencial</option>
                  <option value="domiciliaria">Domiciliaria</option>
                </select>
              </label>
              {renderInput('Motivo de control', hcuForm.clinicalFollowUp.nextAppointmentReason, (value) => updateClinicalFollowUp('nextAppointmentReason', value), 'text', 'Control de evolucion')}
              <div className="sm:col-span-2">
                {renderTextarea('Indicaciones para proxima cita', hcuForm.clinicalFollowUp.nextAppointmentNotes, (value) => updateClinicalFollowUp('nextAppointmentNotes', value), 'Examenes previos, signos de alarma o preparacion para control', 2)}
              </div>
            </div>
          )}
        </>
      ))}

      {formErrors.length > 0 && (
        <div className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <strong className="block">Campos pendientes</strong>
          <ul className="mt-1 list-inside list-disc">
            {formErrors.map((error) => <li key={error}>{error}</li>)}
          </ul>
        </div>
      )}

      {saveMessage && (
        <div className="rounded-sm border border-slate-200 bg-[#f6f9fb] px-3 py-2 text-sm font-semibold text-[#33465b]">
          {saveMessage}
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
        <button className="rounded-sm border border-[#236eaa] px-3 py-2 text-sm font-bold text-[#236eaa] hover:bg-[#edf5fb]" onClick={() => saveMedicalRecord('draft')} disabled={isSaving}>Guardar borrador</button>
        <button className="rounded-sm bg-[#236eaa] px-3 py-2 text-sm font-bold text-white shadow hover:bg-[#1c5d92] disabled:opacity-60" onClick={() => saveMedicalRecord('final')} disabled={isSaving}>Guardar historia</button>
        <button className="rounded-sm border border-[#405165] px-3 py-2 text-sm font-bold text-[#405165] hover:bg-slate-50" onClick={printHcuForm} disabled={isSaving}>Imprimir HCU 002</button>
        <button className="rounded-sm bg-[#d83d43] px-3 py-2 text-sm font-bold text-white hover:bg-[#c93238] disabled:opacity-60" onClick={handleEndConsultation} disabled={isSaving}>Finalizar consulta</button>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen bg-[#eaf0f4] text-[#27384a] ${isMonitorView ? 'consultation-monitor-mode' : ''} ${isClinicalOnlyView ? 'consultation-clinical-mode' : ''}`}>
      <div className={`consultation-room-shell ${isDoctorMenuCollapsed && !isMonitorView && !isClinicalOnlyView ? 'menu-collapsed' : ''}`}>
        {!isMonitorView && !isClinicalOnlyView && (
          <aside className={`consultation-doctor-menu ${isDoctorMenuCollapsed ? 'collapsed' : ''}`}>
            <div className="flex h-[58px] items-center gap-2 border-b border-white/10 bg-[#1d4569] px-3">
              <div className="grid h-6 w-6 place-items-center rounded-sm bg-white text-xs font-black text-[#1d4569]">▰</div>
              <p className="consultation-menu-label text-xl font-bold tracking-wide">Telemedicina</p>
              <button
                type="button"
                className="consultation-menu-toggle"
                onClick={() => setIsDoctorMenuCollapsed((current) => !current)}
                aria-label={isDoctorMenuCollapsed ? 'Abrir menu' : 'Colapsar menu'}
              >
                {isDoctorMenuCollapsed ? '›' : '‹'}
              </button>
            </div>

            <nav className="space-y-1 px-4 py-5">
              {menuItems.map(([label, icon], index) => (
                <button key={label} className={`flex w-full items-center gap-3 rounded px-3 py-2.5 text-left text-[15px] font-medium transition ${index === 0 ? 'bg-[#2b5d8c] text-white' : 'text-[#d9e6f2] hover:bg-white/10'}`}>
                  <span className="grid h-6 w-6 place-items-center text-base">{icon}</span>
                  <span className="consultation-menu-label">{label}</span>
                </button>
              ))}
            </nav>

            <div className="consultation-menu-stats mx-4 border-t border-white/15 pt-4">
              {[
                ['Citas Hoy', '8', '▣'],
                ['Consultas Pendientes', '3', '+'],
                ['Mensajes', '12', '✉'],
                ['Ganancias', '$2,300', '≋'],
              ].map(([label, value, icon]) => (
                <div key={label} className="flex items-center justify-between border-b border-white/10 bg-[#244b70] px-3 py-2 text-sm last:border-b-0">
                  <span className="flex items-center gap-2 text-xs font-semibold text-[#eaf3fb]">
                    <span className="grid h-5 w-5 place-items-center rounded-sm bg-white text-[11px] text-[#244b70]">{icon}</span>
                    <span className="consultation-menu-label">{label}</span>
                  </span>
                  <strong className="consultation-menu-value text-lg leading-none">{value}</strong>
                </div>
              ))}
            </div>
          </aside>
        )}

        <div className="flex min-w-0 flex-col">
          <header className="flex h-[58px] items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm md:px-6">
            <div className="flex items-center gap-8 text-[#6f90aa]">
              <span className="text-2xl">▱</span>
              <span className="h-2 w-2 rounded-full bg-slate-400" />
              <span className="text-2xl">◼</span>
            </div>
            <div className="flex items-center gap-6 text-[#6f90aa]">
              {isDoctor && (
                <button
                  type="button"
                  onClick={isClinicalOnlyView ? openFullConsultationView : openSecondMonitorView}
                  className="rounded-sm border border-[#b8c9d8] bg-[#edf5fb] px-3 py-1.5 text-xs font-bold uppercase text-[#236eaa] hover:bg-[#dcebf6]"
                >
                  {isClinicalOnlyView ? 'Acoplar chat/video' : 'Desmontar chat/video'}
                </button>
              )}
              <span className="text-xl">●</span>
              <span className="grid h-6 w-6 place-items-center rounded-full bg-[#6f90aa] text-xs font-bold text-white">i</span>
              <span className="text-xl">⚙</span>
              <div className="grid h-9 w-9 place-items-center rounded-full bg-[#d9e5ef] text-sm font-bold text-[#2a5b86]">
                {user.name?.slice(0, 1) || 'U'}
              </div>
            </div>
          </header>

          <div className="consultation-workspace">
            <aside className="consultation-chat-column">
              <section className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                <div className="flex items-start gap-3">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-[#d8e6ef] text-lg font-bold text-[#2b5d8c]">
                    {(activePerson.name || 'P').slice(0, 1)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h2 className="truncate text-[17px] font-bold text-[#33465b]">{activePerson.name || otherUserName}</h2>
                      <span className="rounded-sm bg-[#edf5fb] px-2 py-1 text-[11px] font-bold uppercase text-[#236eaa]">
                        {hcuForm.recordStatus === 'final' ? 'Final' : 'Borrador'}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[#59697a]">Historia: {hcuForm.facilityPatientData.clinicalHistoryNumber || 'Sin numero'}</p>
                    <p className="text-xs text-[#59697a]">Duracion: {formatDuration(callSeconds)}</p>
                  </div>
                </div>
              </section>

              {unreadChatCount > 0 && (
                <button
                  type="button"
                  onClick={focusChatPanel}
                  className="consultation-chat-alert"
                >
                  {unreadChatCount} mensaje(s) nuevo(s)
                </button>
              )}

              <section ref={chatPanelRef} className="consultation-chat-sticky consultation-whatsapp-chat rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                <div className="mb-3 flex items-start justify-between gap-3 border-b border-slate-200 pb-2">
                  <div>
                    <h3 className="text-xl font-bold text-[#405165]">Chat clinico</h3>
                    <p className="text-xs text-slate-500">Mensajes durante la atencion.</p>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {isDoctor && !isMonitorView && !isClinicalOnlyView && (
                      <button
                        type="button"
                        onClick={openSecondMonitorView}
                        className="rounded-sm border border-[#b8c9d8] bg-[#edf5fb] px-2 py-1 text-[11px] font-bold uppercase text-[#236eaa] hover:bg-[#dcebf6]"
                      >
                        Desmontar
                      </button>
                    )}
                    <span className="rounded-sm bg-[#edf5fb] px-2 py-1 text-[11px] font-bold uppercase text-[#236eaa]">
                      {chatTranscriptLabel}
                    </span>
                    <span className="rounded-sm bg-[#f0fdfa] px-2 py-1 text-[11px] font-bold uppercase text-[#0f766e]">
                      {clinicalFilesLabel}
                    </span>
                  </div>
                </div>

                {clinicalFileEvents.length > 0 && (
                  <div className="consultation-file-events" aria-label="Documentos clinicos de la consulta">
                    <div className="consultation-file-events-header">
                      <div>
                        <strong>Documentos clinicos</strong>
                        <span>Adjuntos separados del chat.</span>
                      </div>
                      <button type="button" onClick={openClinicalFilesFromAlert}>
                        Abrir panel
                      </button>
                    </div>
                    {latestClinicalFileAlert && (
                      <div className="consultation-file-alert">
                        <div>
                          <strong>Nuevo documento recibido</strong>
                          <span>
                            {latestClinicalFileAlert.originalName || 'Archivo clinico'} ·{' '}
                            {latestClinicalFileAlert.uploadedByName || latestClinicalFileAlert.uploadedByRole || 'Paciente'}
                          </span>
                          {latestClinicalFileAlert.notes && <p>{latestClinicalFileAlert.notes}</p>}
                        </div>
                        <button type="button" onClick={openClinicalFilesFromAlert}>
                          Ver documento
                        </button>
                      </div>
                    )}
                    <div className="consultation-file-event-list">
                      {clinicalFileEvents.slice(0, 3).map((fileRecord) => (
                        <button
                          key={fileRecord.id}
                          type="button"
                          className="consultation-file-event-item"
                          onClick={openClinicalFilesFromAlert}
                        >
                          <span>{fileRecord.originalName || 'Archivo clinico'}</span>
                          <small>
                            {fileRecord.uploadedByRole === 'doctor' ? 'Medico' : 'Paciente'} · {formatClinicalFileTime(fileRecord.createdAt)}
                          </small>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div ref={chatMessagesRef} className="consultation-chat-messages">
                  {chatMessages.length === 0 ? (
                    <div className="py-8 text-center text-sm text-slate-500">
                      Aun no hay mensajes en esta consulta.
                    </div>
                  ) : (
                    chatMessages.map((message) => {
                      const isOwnMessage = message.author?.id === user.id;
                      return (
                        <div key={message.id} className={`consultation-chat-row ${isOwnMessage ? 'own' : 'other'}`}>
                          <div className="consultation-chat-bubble">
                            <div className="consultation-chat-meta">
                              <span>{message.author?.role === 'doctor' ? 'Medico' : 'Paciente'} · {message.author?.name || 'Usuario'}</span>
                              <span>{formatChatTime(message.createdAt)}</span>
                            </div>
                            <p>{message.text}</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                <form className="consultation-chat-form" onSubmit={sendChatMessage}>
                  <input
                    type="text"
                    value={chatText}
                    onChange={(event) => setChatText(event.target.value)}
                    maxLength={1000}
                    placeholder="Escribir mensaje..."
                  />
                  <button type="submit" disabled={isSendingChat || !chatText.trim()}>
                    Enviar
                  </button>
                </form>
                {chatSaveStatus && (
                  <p className="mt-2 text-xs font-semibold text-[#59697a]">{chatSaveStatus}</p>
                )}
              </section>
            </aside>

            <main className="consultation-video-column">
              <div className={`relative w-full max-w-[720px] overflow-hidden rounded bg-[#172536] shadow-[0_12px_32px_rgba(39,56,74,0.28)] ${isVideoFloating ? 'consultation-video-floating' : ''}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-[#101c2a] px-4 py-3 text-white">
                  <div className="min-w-0">
                    <p className="text-[11px] font-bold uppercase text-[#9db6ca]">Videoconsulta</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                      <span className="max-w-[190px] truncate font-bold">Paciente: {patientName}</span>
                      <span className="text-[#6f8398]">|</span>
                      <span className="max-w-[190px] truncate font-bold">Medico: {doctorName}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {!isMonitorView && !isClinicalOnlyView && (
                      <button
                        type="button"
                        onClick={() => setIsVideoFloating((current) => !current)}
                        className="rounded-sm border border-white/20 px-2.5 py-1 font-bold uppercase text-[#d9ecfb] hover:bg-white/10"
                      >
                        {isVideoFloating ? 'Anclar video' : 'Video flotante'}
                      </button>
                    )}
                    {isDoctor && !isClinicalOnlyView && (
                      <button
                        type="button"
                        onClick={isMonitorView ? openFullConsultationView : openSecondMonitorView}
                        className="rounded-sm border border-white/20 px-2.5 py-1 font-bold uppercase text-[#d9ecfb] hover:bg-white/10"
                      >
                        {isMonitorView ? 'Acoplar' : 'Desmontar chat/video'}
                      </button>
                    )}
                    <span className={`rounded-sm px-2.5 py-1 font-bold uppercase ${
                      consultationStatus === 'active'
                        ? 'bg-emerald-500 text-white'
                        : consultationStatus === 'error' || consultationStatus === 'ended'
                          ? 'bg-[#d83d43] text-white'
                          : consultationStatus === 'reconnecting'
                            ? 'bg-amber-400 text-[#2f2f2f]'
                            : 'bg-[#edf5fb] text-[#236eaa]'
                    }`}>
                      {connectionLabel}
                    </span>
                    <span className="rounded-sm bg-white/10 px-2.5 py-1 font-mono text-sm font-bold">{formatDuration(callSeconds)}</span>
                  </div>
                </div>
                {isLiveKitProvider && recordingSettings.enabled && (
                  <div className={`absolute right-4 top-[88px] z-20 max-w-[260px] rounded border px-3 py-2 text-xs font-bold shadow-lg md:top-[82px] ${
                    recordingState.status === 'recording'
                      ? 'border-red-300 bg-red-50 text-red-700'
                      : recordingState.status === 'error'
                        ? 'border-amber-300 bg-amber-50 text-amber-800'
                        : 'border-white/20 bg-slate-800 text-white'
                  }`}>
                    <span className="block uppercase">
                      {recordingState.status === 'recording' ? 'Grabando' : 'Grabacion'}
                    </span>
                    <small className="block font-semibold">{recordingState.message}</small>
                  </div>
                )}

                <div className="absolute left-4 top-[88px] z-20 w-[185px] overflow-hidden rounded border border-white/20 bg-slate-800 shadow-lg md:top-[82px] md:w-[210px]">
                  <video ref={localVideoRef} autoPlay muted playsInline className="aspect-video w-full object-cover" />
                  {!isVideoOn && !isScreenSharing && (
                    <div className="absolute inset-0 grid place-items-center bg-[#111827] text-center text-xs font-bold text-white">
                      Camara apagada
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-black/60 px-2 py-1 text-[11px] font-bold text-white">
                    <span>{user.name || 'Yo'}</span>
                    <span>{isScreenSharing ? 'Pantalla' : 'Vista local'}</span>
                  </div>
                </div>

                <div className="relative aspect-[4/3] bg-[#19283a]">
                  <video ref={remoteVideoRef} autoPlay playsInline className="h-full w-full object-cover" />
                  <audio ref={remoteAudioRef} autoPlay />
                  {consultationStatus === 'active' && (
                    <div className="absolute bottom-4 left-4 rounded-sm bg-black/60 px-3 py-2 text-sm font-bold text-white">
                      {otherUserName || activePerson.name || 'Participante'}
                    </div>
                  )}

                  {consultationStatus !== 'active' && (
                    <div className="absolute inset-0 grid place-items-center bg-[linear-gradient(135deg,#e8eef2,#f8fafc)]">
                      <div className="px-8 text-center text-[#263747]">
                        <div className="mx-auto mb-4 grid h-20 w-20 place-items-center rounded-full bg-[#d6e4ee] text-2xl font-bold text-[#2a5b86]">VC</div>
                        <h2 className="text-2xl font-semibold">{otherUserName || 'Esperando conexion'}</h2>
                        <p className="mt-2 text-sm text-slate-500">
                          {connectionDetail}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex h-auto flex-wrap items-center justify-center gap-1 bg-[#1d2d3d] px-4 py-2 text-white">
                  <button className={`flex h-9 items-center gap-2 rounded-sm px-4 text-sm font-semibold transition ${isAudioOn ? 'bg-white/5 hover:bg-white/10' : 'bg-red-600'}`} onClick={toggleAudio} type="button">
                    <span>{isAudioOn ? '●' : '×'}</span> {isAudioOn ? 'Microfono' : 'Mic apagado'}
                  </button>
                  <button className={`flex h-9 items-center gap-2 rounded-sm px-4 text-sm font-semibold transition ${isVideoOn ? 'bg-white/5 hover:bg-white/10' : 'bg-red-600'}`} onClick={toggleVideo} type="button">
                    <span>{isVideoOn ? '▣' : '×'}</span> {isVideoOn ? 'Camara' : 'Camara apagada'}
                  </button>
                  <button
                    className={`flex h-9 items-center gap-2 rounded-sm px-4 text-sm font-semibold transition ${isScreenSharing ? 'bg-[#236eaa]' : 'bg-white/5 hover:bg-white/10'}`}
                    onClick={toggleScreenShare}
                    type="button"
                  >
                    <span>▱</span> {isScreenSharing ? 'Dejar pantalla' : 'Compartir pantalla'}
                  </button>
                  {isDoctor && isLiveKitProvider && recordingSettings.enabled && ['recording', 'starting'].includes(recordingState.status) && (
                    <button className="flex h-9 items-center gap-2 rounded-sm bg-white/5 px-4 text-sm font-semibold transition hover:bg-white/10" onClick={stopRecording} type="button">
                      Detener grabacion
                    </button>
                  )}
                  <button className="ml-2 h-9 rounded-sm bg-[#d83d43] px-8 text-sm font-bold text-white hover:bg-[#c93238]" onClick={handleEndConsultation} type="button">
                    Finalizar
                  </button>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-white/10 bg-[#132131] px-4 py-2 text-xs text-[#c9d8e4]">
                  <span>Estado de conexion: <strong className="text-white">{connectionLabel}</strong></span>
                  {isLiveKitProvider && recordingSettings.enabled && (
                    <span>Grabacion: <strong className="text-white">{recordingState.message}</strong></span>
                  )}
                  <span>{connectionDetail}</span>
                </div>
              </div>
            </main>

            <aside className="consultation-forms-column">
              <div className="consultation-side-tabs" role="tablist" aria-label="Paneles de consulta">
                {sidePanels.map(([key, label, meta]) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={activeSidePanel === key}
                    className={activeSidePanel === key ? 'active' : ''}
                    onClick={() => setActiveSidePanel(key)}
                  >
                    <strong>{label}</strong>
                    <span>{meta}</span>
                  </button>
                ))}
              </div>

              {isDoctor && activeSidePanel === 'patient' && (
                <section className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                  <ClinicalAlertsPanel alerts={clinicalAlerts} compact />
                </section>
              )}

              {isDoctor && activeSidePanel === 'admission' && (
                <section className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                  <div className="mb-3 flex items-start justify-between gap-3 border-b border-slate-200 pb-2">
                    <div>
                      <h3 className="text-xl font-bold text-[#405165]">Admision virtual</h3>
                      <p className="text-xs text-slate-500">Revision previa antes de iniciar la atencion.</p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      <span className={`rounded-sm px-2 py-1 text-[11px] font-bold uppercase ${admissionReady ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {admissionReady ? 'Lista' : 'Pendiente'}
                      </span>
                      <span className={`triage-priority triage-priority-${preTriage?.priority || 'baja'}`}>
                        {preTriage ? getTriagePriorityLabel(preTriage.priority) : 'Pretriaje pendiente'}
                      </span>
                    </div>
                  </div>
                  <PreTriagePanel
                    appointment={{ ...appointment, preTriage }}
                    user={user}
                    compact
                    initialValue={preTriage}
                  />
                  <div className="grid grid-cols-2 gap-2 text-xs text-[#44576b]">
                    <div className="rounded-sm border border-slate-200 bg-[#f8fafc] p-2">
                      <strong className="block text-[#263746]">Datos</strong>
                      <span>{admissionChecks.patientDataVerified ? 'Verificados' : 'Pendientes'}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-[#f8fafc] p-2">
                      <strong className="block text-[#263746]">Documentos</strong>
                      <span>{admissionChecks.documentsVerified ? `${admissionFileCount} revisado(s)` : `${admissionFileCount} por revisar`}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-[#f8fafc] p-2">
                      <strong className="block text-[#263746]">Consentimiento</strong>
                      <span>{admissionChecks.consentVerified ? 'Confirmado' : 'Pendiente'}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-[#f8fafc] p-2">
                      <strong className="block text-[#263746]">Motivo</strong>
                      <span>{admissionChecks.consultationReasonVerified ? 'Validado' : 'Pendiente'}</span>
                    </div>
                  </div>
                  <div className="mt-3 space-y-2 text-sm text-[#44576b]">
                    <p><strong>Motivo:</strong> {admission?.consultationReason || hcuForm.consultationReason.description || 'No registrado'}</p>
                    <p><strong>Notas admision:</strong> {admission?.notes || 'Sin notas administrativas'}</p>
                    {admission?.updatedAt && (
                      <p className="text-xs text-slate-500">Actualizada: {new Date(admission.updatedAt).toLocaleString('es-EC')}</p>
                    )}
                  </div>
                </section>
              )}

              {isDoctor && activeSidePanel === 'patient' && (
                <section className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                  <div className="mb-3 flex items-start justify-between gap-3 border-b border-slate-200 pb-2">
                    <div>
                      <h3 className="text-xl font-bold text-[#405165]">Panel medico lateral</h3>
                      <p className="text-xs text-slate-500">Vista rapida para decisiones durante la atencion.</p>
                    </div>
                    <span className="rounded-sm bg-[#edf5fb] px-2 py-1 text-[11px] font-bold uppercase text-[#236eaa]">
                      Clinico
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-[#44576b]">
                    <div className="rounded-sm border border-slate-200 bg-[#f8fafc] p-2">
                      <strong className="block text-[#263746]">Paciente</strong>
                      <span>{medicalPanelPatient.name || patientName}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-[#f8fafc] p-2">
                      <strong className="block text-[#263746]">Documento</strong>
                      <span>{medicalPanelPatient.documentId || 'Pendiente'}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-[#f8fafc] p-2">
                      <strong className="block text-[#263746]">Edad / sexo</strong>
                      <span>{patientAge || 'Edad pendiente'} · {medicalPanelPatient.sex || 'Sexo pendiente'}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-[#f8fafc] p-2">
                      <strong className="block text-[#263746]">Contacto</strong>
                      <span>{medicalPanelPatient.phone || medicalPanelPatient.email || 'Sin contacto'}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-[#f8fafc] p-2">
                      <strong className="block text-[#263746]">Prioridad</strong>
                      <span>{medicalPanelPatient.priorityGroup || 'Ninguno'}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-[#f8fafc] p-2">
                      <strong className="block text-[#263746]">Discapacidad</strong>
                      <span>{medicalPanelPatient.disability || 'No'} {medicalPanelPatient.disabilityDetail || ''}</span>
                    </div>
                  </div>

                  <div className="mt-3 space-y-2 text-sm text-[#44576b]">
                    <div className="rounded-sm border border-red-200 bg-red-50 p-2">
                      <strong className="block text-red-700">Alergias</strong>
                      <span>{allergySummary}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-white p-2">
                      <strong className="block text-[#263746]">Medicamentos actuales</strong>
                      <span>{medicationSummary}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-white p-2">
                      <strong className="block text-[#263746]">Antecedentes personales</strong>
                      <span>{personalHistorySummary}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-white p-2">
                      <strong className="block text-[#263746]">Antecedentes familiares</strong>
                      <span>{familyHistorySummary}</span>
                    </div>
                    <div className="rounded-sm border border-slate-200 bg-white p-2">
                      <strong className="block text-[#263746]">Emergencia</strong>
                      <span>
                        {medicalPanelPatient.emergencyContact
                          ? `${medicalPanelPatient.emergencyContact} · ${medicalPanelPatient.emergencyRelationship || 'Contacto'} · ${medicalPanelPatient.emergencyPhone || 'Telefono pendiente'}`
                          : 'Sin contacto de emergencia registrado'}
                      </span>
                    </div>
                  </div>

                  <div className="mt-3 border-t border-slate-200 pt-3">
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="text-sm font-bold text-[#263746]">Consultas anteriores</h4>
                      <span className="text-[11px] font-bold uppercase text-slate-500">{previousRecords.length}</span>
                    </div>
                    {previousRecords.length === 0 ? (
                      <p className="rounded-sm bg-[#f8fafc] p-2 text-sm text-slate-500">Sin consultas previas registradas.</p>
                    ) : (
                      <div className="max-h-[150px] space-y-2 overflow-y-auto">
                        {previousRecords.slice(0, 4).map((record) => (
                          <div key={record.id} className="rounded-sm border border-slate-200 p-2 text-xs text-[#44576b]">
                            <strong className="block text-[#263746]">{getRecordMainDiagnosis(record)}</strong>
                            <span>{record.evolution || record.hcu002?.clinicalFollowUp?.evolution || record.hcu002?.consultationReason?.description || 'Sin resumen evolutivo'}</span>
                            <small className="mt-1 block text-slate-400">
                              {record.updatedAt || record.savedAt ? new Date(record.updatedAt || record.savedAt).toLocaleDateString('es-EC') : 'Fecha pendiente'}
                            </small>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 border-t border-slate-200 pt-3">
                    <button type="button" onClick={() => handleQuickAction('prescription')} className="rounded-sm bg-[#236eaa] px-2 py-2 text-xs font-bold text-white hover:bg-[#1c5d92]">
                      Receta
                    </button>
                    <button type="button" onClick={() => handleQuickAction('order')} className="rounded-sm border border-[#236eaa] px-2 py-2 text-xs font-bold text-[#236eaa] hover:bg-[#edf5fb]">
                      Orden
                    </button>
                    <button type="button" onClick={() => handleQuickAction('certificate')} className="rounded-sm border border-[#236eaa] px-2 py-2 text-xs font-bold text-[#236eaa] hover:bg-[#edf5fb]">
                      Certificado
                    </button>
                    <button type="button" onClick={() => handleQuickAction('referral')} className="rounded-sm border border-[#405165] px-2 py-2 text-xs font-bold text-[#405165] hover:bg-slate-50">
                      Referencia
                    </button>
                  </div>
                  {quickActionMessage && (
                    <p className="mt-2 rounded-sm bg-[#edf5fb] p-2 text-xs font-semibold text-[#236eaa]">{quickActionMessage}</p>
                  )}
                </section>
              )}

              {isDoctor && activeSidePanel === 'summary' && (
                <section className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                  <div className="mb-3 flex items-start justify-between gap-3 border-b border-slate-200 pb-2">
                    <div>
                      <h3 className="text-xl font-bold text-[#405165]">Resumen automatico</h3>
                      <p className="text-xs text-slate-500">Se genera al finalizar y queda guardado en la historia clinica.</p>
                    </div>
                    <button
                      type="button"
                      onClick={previewConsultationSummary}
                      className="rounded-sm border border-[#236eaa] px-2 py-1 text-[11px] font-bold uppercase text-[#236eaa] hover:bg-[#edf5fb]"
                    >
                      Previsualizar
                    </button>
                  </div>
                  {consultationSummary ? (
                    <div className="space-y-2 text-sm text-[#44576b]">
                      <p><strong>Motivo:</strong> {consultationSummary.reason}</p>
                      <p><strong>Diagnostico:</strong> {consultationSummary.diagnosis ? `${consultationSummary.diagnosis.code || ''} ${consultationSummary.diagnosis.description || ''}`.trim() : 'Pendiente'}</p>
                      <p><strong>Tratamiento:</strong> {consultationSummary.treatment}</p>
                      <p><strong>Recomendaciones:</strong> {consultationSummary.recommendations}</p>
                      <p><strong>Signos de alarma:</strong> {consultationSummary.alarmSigns}</p>
                      <p><strong>Receta:</strong> {consultationSummary.prescriptions.length > 0
                        ? `${consultationSummary.prescriptions.length} receta(s), ${consultationSummary.prescriptions.reduce((total, prescription) => total + prescription.medications.length, 0)} medicamento(s)`
                        : 'Sin receta digital emitida'}</p>
                      <p><strong>Proxima cita:</strong> {consultationSummary.nextAppointment
                        ? `${consultationSummary.nextAppointment.date || 'Fecha pendiente'} ${consultationSummary.nextAppointment.time || ''} · ${consultationSummary.nextAppointment.reason || 'Motivo pendiente'}`
                        : 'No requerida'}</p>
                      <p><strong>Archivos relevantes:</strong> {consultationSummary.relevantFiles.length > 0
                        ? consultationSummary.relevantFiles.map((file) => file.name).join('; ')
                        : 'Sin archivos adjuntos'}</p>
                    </div>
                  ) : (
                    <p className="rounded-sm bg-[#f8fafc] p-2 text-sm text-slate-500">
                      El resumen se construira automaticamente al guardar/finalizar la consulta. Puedes previsualizarlo antes del cierre.
                    </p>
                  )}
                </section>
              )}

              {((isDoctor && activeSidePanel === 'documents') || (!isDoctor && activeSidePanel === 'files')) && (
              <section className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                <ClinicalFilesPanel
                  appointmentId={appointmentId}
                  patientId={appointment.patient?.id}
                  patientName={appointment.patient?.name}
                  user={user}
                  canUpload={user.role === 'patient'}
                  compact
                  refreshSignal={clinicalFilesRefresh}
                />
              </section>
              )}

              {isDoctor && activeSidePanel === 'prescription' && (
                <section ref={prescriptionPanelRef} className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                  <DigitalPrescriptionPanel
                    appointmentId={appointmentId}
                    patient={{
                      id: medicalPanelPatient.id || appointment.patient?.id,
                      name: medicalPanelPatient.name || appointment.patient?.name,
                      documentId: medicalPanelPatient.documentId || hcuForm.facilityPatientData.documentId,
                      age: patientAge,
                      sex: medicalPanelPatient.sex || hcuForm.facilityPatientData.sex,
                    }}
                    doctor={{
                      id: user.id,
                      name: user.name || doctor.name,
                      specialty: doctor.specialty || user.specialty || 'Medicina General',
                      license: doctor.documentId || user.documentId || '',
                      signature: user.name || doctor.name,
                    }}
                    user={user}
                  />
                </section>
              )}

              {isDoctor && activeSidePanel === 'orders' && (
                <section ref={medicalOrdersPanelRef} className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                  <MedicalOrdersPanel
                    appointmentId={appointmentId}
                    patient={{
                      id: medicalPanelPatient.id || appointment.patient?.id,
                      name: medicalPanelPatient.name || appointment.patient?.name,
                      documentId: medicalPanelPatient.documentId || hcuForm.facilityPatientData.documentId,
                      age: patientAge,
                      sex: medicalPanelPatient.sex || hcuForm.facilityPatientData.sex,
                    }}
                    doctor={{
                      id: user.id,
                      name: user.name || doctor.name,
                      specialty: doctor.specialty || user.specialty || 'Medicina General',
                      license: doctor.documentId || user.documentId || '',
                      signature: user.name || doctor.name,
                    }}
                    user={user}
                    defaultDiagnosis={currentPrimaryDiagnosis}
                  />
                </section>
              )}

              {isDoctor && activeSidePanel === 'certificates' && (
                <section ref={medicalCertificatesPanelRef} className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                  <MedicalCertificatesPanel
                    appointmentId={appointmentId}
                    patient={{
                      id: medicalPanelPatient.id || appointment.patient?.id,
                      name: medicalPanelPatient.name || appointment.patient?.name,
                      documentId: medicalPanelPatient.documentId || hcuForm.facilityPatientData.documentId,
                      age: patientAge,
                      sex: medicalPanelPatient.sex || hcuForm.facilityPatientData.sex,
                    }}
                    doctor={{
                      id: user.id,
                      name: user.name || doctor.name,
                      specialty: doctor.specialty || user.specialty || 'Medicina General',
                      license: doctor.documentId || user.documentId || '',
                      signature: user.name || doctor.name,
                    }}
                    user={user}
                    defaultDiagnosis={currentPrimaryDiagnosis}
                  />
                </section>
              )}

              {isDoctor && activeSidePanel === 'referral' && (
                <section ref={referralPanelRef} className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                  <InterconsultationReferralPanel
                    appointmentId={appointmentId}
                    patient={{
                      id: medicalPanelPatient.id || appointment.patient?.id,
                      name: medicalPanelPatient.name || appointment.patient?.name,
                      documentId: medicalPanelPatient.documentId || hcuForm.facilityPatientData.documentId,
                      clinicalHistoryNumber: medicalPanelPatient.clinicalHistoryNumber || hcuForm.facilityPatientData.clinicalHistoryNumber,
                      age: patientAge,
                      sex: medicalPanelPatient.sex || hcuForm.facilityPatientData.sex,
                    }}
                    doctor={{
                      id: user.id,
                      name: user.name || doctor.name,
                      specialty: doctor.specialty || user.specialty || 'Medicina General',
                      license: doctor.documentId || user.documentId || '',
                      signature: user.name || doctor.name,
                    }}
                    user={user}
                    defaultDiagnosis={currentPrimaryDiagnosis}
                    defaultClinicalSummary={referralClinicalSummary}
                  />
                </section>
              )}

              {activeSidePanel === 'hcu' && (
              <section className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                <div className="mb-3 flex items-center justify-between border-b border-slate-200 pb-2">
                  <div>
                    <h3 className="text-xl font-bold text-[#405165]">HCU 002</h3>
                    <p className="text-xs text-slate-500">Consulta externa - anamnesis, examen fisico y prescripciones</p>
                  </div>
                  <div className="text-right">
                    <span className="block text-lg font-black text-[#236eaa]">{formCompletion.percentage}%</span>
                    <span className="text-[11px] font-bold uppercase text-slate-500">
                      {formCompletion.completedModules}/{formCompletion.totalModules} modulos
                    </span>
                  </div>
                </div>
                {isDoctor && (
                  <div className="mb-3 grid grid-cols-2 gap-2 md:grid-cols-4">
                    {formCompletion.modules.map((module) => (
                      <button
                        type="button"
                        key={module.key}
                        onClick={() => setOpenSections((current) => ({ ...current, [module.key]: true }))}
                        className={`rounded-sm border px-2 py-2 text-left text-xs transition ${
                          module.completion.completed
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                            : 'border-amber-200 bg-amber-50 text-amber-800'
                        }`}
                      >
                        <strong className="block">{module.code}</strong>
                        <span className="block truncate">{module.title}</span>
                        <span className="mt-1 block font-bold">
                          {module.completion.completed ? 'Completo' : `${module.completion.pending.length} pendiente(s)`}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {isDoctor ? renderHcuForm() : renderPatientSummary()}
              </section>
              )}

              {activeSidePanel === 'hcu' && (
              <section className="rounded bg-white p-4 shadow-[0_4px_18px_rgba(39,56,74,0.12)]">
                <div className="mb-3 flex items-center justify-between border-b border-slate-200 pb-2">
                  <h3 className="text-xl font-bold text-[#405165]">Resumen clinico</h3>
                  <span className="text-xl leading-none text-slate-400">•••</span>
                </div>
                <div className="space-y-2 text-sm text-[#44576b]">
                  <p><strong>Paciente:</strong> {hcuForm.facilityPatientData.fullName || patient.name || 'Paciente'}</p>
                  <p><strong>Motivo:</strong> {hcuForm.consultationReason.description || 'Pendiente'}</p>
                  <p><strong>IMC:</strong> {hcuForm.vitalSigns.bmi || 'Pendiente'} Kg/m2</p>
                  <p><strong>Antecedentes personales:</strong> {selectedSummary(hcuForm.personalHistory.items, historyOptions).join('; ') || hcuForm.personalHistory.clinicalDetails || 'Sin registro'}</p>
                  <p><strong>Revision por sistemas:</strong> {selectedSummary(hcuForm.organSystemsReview, organSystemOptions).join('; ') || 'Sin patologias marcadas'}</p>
                  <p><strong>Evolucion:</strong> {hcuForm.clinicalFollowUp?.evolution || 'Pendiente'}</p>
                  <p><strong>Proxima cita:</strong> {hcuForm.clinicalFollowUp?.requiresNextAppointment
                    ? `${hcuForm.clinicalFollowUp.nextAppointmentDate || 'Fecha pendiente'} ${hcuForm.clinicalFollowUp.nextAppointmentTime || ''}`
                    : 'No requerida'}</p>
                </div>
              </section>
              )}
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationRoom;
